import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  FiActivity,
  FiBarChart2,
  FiArrowRight,
  FiBriefcase,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronLeft,
  FiCreditCard,
  FiCpu,
  FiDatabase,
  FiFileText,
  FiHeart,
  FiImage,
  FiInbox,
  FiEdit2,
  FiDownload,
  FiEye,
  FiGrid,
  FiGlobe,
  FiLayers,
  FiMessageSquare,
  FiPackage,
  FiPlus,
  FiSearch,
  FiSettings,
  FiShoppingBag,
  FiTag,
  FiTrendingUp,
  FiTrash2,
  FiUploadCloud,
  FiUserPlus,
  FiUsers,
  FiX
} from 'react-icons/fi';
import { api } from '../services/api';
import ProductRichTextEditor from '../components/ProductRichTextEditor';
import AdminFieldTranslation from '../components/AdminFieldTranslation';
import AdminMarketPrice from '../components/AdminMarketPrice';
import AdminProductImagesTranslation from '../components/AdminProductImagesTranslation';
import AdminProductDescriptionTranslation from '../components/AdminProductDescriptionTranslation';
import AdminProductTranslationPublish from '../components/AdminProductTranslationPublish';
import AdminArticleTranslationPublish from '../components/AdminArticleTranslationPublish';
import AdminArticleContentTranslation from '../components/AdminArticleContentTranslation';
import AdminLocales from '../components/AdminLocales';
import AdminSettingsPanel from '../components/AdminSettingsPanel';
import AdminServiceEditor from '../components/AdminServiceEditor';
import AdminServiceOrders from '../components/AdminServiceOrders';
import AdminTransactions from '../components/AdminTransactions';
import AdminContactMessages from '../components/AdminContactMessages';
import AdminProductReviews from '../components/AdminProductReviews';
import HomeProductShowcaseSettings from '../components/HomeProductShowcaseSettings';
import AdminAnalytics from '../components/AdminAnalytics';
import AdminAiContentAgent from '../components/AdminAiContentAgent';
import AdminBackups from '../components/AdminBackups';
import ServiceIcon from '../components/ServiceIcon';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { hasRichTextContent, richTextToPlainText } from '../utils/richText';
import { getDirectMediaUrlInputValue } from '../utils/mediaUrl';
import { createEmptyService, hydrateService, hydrateServices, serializeService, resolveServiceMediaUrl } from '../utils/serviceContent';
import './AdminPanel.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const norm = (item) => ({ ...item, id: item._id || item.id });
const PRODUCT_ORDER_STATUSES = [
  'در انتظار پرداخت',
  'آماده دانلود',
  'لغو شده',
  'بازپرداخت شده',
];
const emptyManualOrderPayment = (amount = '') => ({
  amount,
  paidAt: new Date().toISOString().slice(0, 10),
  paymentMethod: 'card_to_card',
  reference: '',
  note: '',
});
const emptyOrderForm = () => ({
  customer: '',
  product: '',
  customerId: '',
  productId: '',
  amount: '',
  status: 'در انتظار پرداخت',
});
const ADMIN_TABS = [
  {
    id: 'dashboard',
    label: 'داشبورد',
    description: 'نمای کلی عملکرد و آخرین فعالیت‌های وب‌سایت',
    icon: FiGrid,
  },
  {
    id: 'analytics',
    label: 'آمار و تحلیل',
    description: 'بازدیدها، افراد آنلاین و رفتار لحظه‌ای کاربران',
    icon: FiBarChart2,
  },
  {
    id: 'users',
    label: 'مدیریت کاربران',
    description: 'حساب‌های کاربری و سطح دسترسی اعضا',
    icon: FiUsers,
  },
  {
    id: 'services',
    label: 'مدیریت خدمات',
    description: 'معرفی و ویرایش خدمات قابل ارائه',
    icon: FiBriefcase,
  },
  {
    id: 'products',
    label: 'مدیریت محصولات',
    description: 'محصولات دانلودی، قیمت‌گذاری و فایل‌های فروش',
    icon: FiPackage,
  },
  {
    id: 'articles',
    label: 'مدیریت مقالات',
    description: 'محتوا، دسته‌بندی و انتشار مقالات',
    icon: FiFileText,
  },
  {
    id: 'ai-content-agent',
    label: 'Agent مقاله‌نویس',
    description: 'تحقیق، تولید، SEO، ترجمه و لینک‌سازی هوشمند مقالات',
    icon: FiCpu,
  },
  {
    id: 'orders',
    label: 'سفارش محصولات',
    description: 'پیگیری خریدها، پرداخت‌ها و وضعیت سفارش‌ها',
    icon: FiShoppingBag,
  },
  {
    id: 'service-orders',
    label: 'سفارش خدمات',
    description: 'بررسی درخواست‌ها، پیشنهاد قیمت و مدیریت اجرای پروژه',
    icon: FiBriefcase,
  },
  {
    id: 'finance',
    label: 'امور مالی',
    description: 'تراکنش‌ها، روش‌های پرداخت و سوابق مالی سفارش‌ها',
    icon: FiCreditCard,
  },
  {
    id: 'reviews',
    label: 'نظرات محصولات',
    description: 'بررسی، تأیید و مدیریت تجربه‌های ثبت‌شده کاربران',
    icon: FiMessageSquare,
  },
  {
    id: 'contact-messages',
    label: 'پیام‌های تماس',
    description: 'پیام‌های فرم ارتباط با ما و پیگیری پاسخ‌ها',
    icon: FiInbox,
  },
  {
    id: 'backups',
    label: 'پشتیبان‌گیری',
    description: 'بک‌آپ کامل، خروجی داده‌ها و بازیابی کنترل‌شده',
    icon: FiDatabase,
  },
  {
    id: 'locales',
    label: 'زبان‌ها و ترجمه‌ها',
    description: 'فعال‌سازی زبان‌ها و مدیریت زیرساخت ترجمه محتوا',
    icon: FiGlobe,
  },
  {
    id: 'settings',
    label: 'تنظیمات',
    description: 'برند، ارتباطات، سرویس‌ها و رفتار سراسری وب‌سایت',
    icon: FiSettings,
  },
];
const PRODUCT_IMAGE_FIELDS = [
  {
    name: 'cardImageUrl',
    uploadId: 'product-card-image-upload',
    urlId: 'product-card-image-url',
    title: 'تصویر کارت محصول',
    badge: 'مربعی ۱:۱',
    help: 'در فهرست محصولات و کارت‌های فروشگاه نمایش داده می‌شود.',
    placeholder: 'https://example.com/product-card.webp',
    previewClass: 'is-square',
  },
  {
    name: 'detailImageUrl',
    uploadId: 'product-detail-image-upload',
    urlId: 'product-detail-image-url',
    title: 'تصویر صفحه محصول',
    badge: 'افقی ۱۶:۹',
    help: 'به‌صورت بنر مستطیلی در بالای صفحه جزئیات محصول نمایش داده می‌شود.',
    placeholder: 'https://example.com/product-banner.webp',
    previewClass: 'is-banner',
  },
];
const emptyProductForm = (category = '') => ({
  name: '',
  slug: '',
  description: '',
  price: '',
  discountPrice: '',
  cardImageUrl: '',
  detailImageUrl: '',
  features: '',
  category,
  seoTitle: '',
  seoDescription: '',
  version: '1.0.0',
  releaseDate: '',
  lastUpdated: '',
  changelog: [],
  faq: [],
});
const emptyArticleForm = (category = '') => ({
  title: '',
  slug: '',
  excerpt: '',
  summary: '',
  content: '',
  category,
  imageUrl: '',
  imageAlt: '',
  seoTitle: '',
  seoDescription: '',
  primaryKeyword: '',
  secondaryKeywords: [],
  faq: [],
  internalLinks: [],
  relatedArticles: [],
  schemaTypes: ['Article', 'BreadcrumbList'],
  cta: { title: '', text: '', label: '', url: '' },
  publicationStatus: 'published',
});
const PRODUCT_EDITOR_TABS = [
  { id: 'overview', label: 'اطلاعات و قیمت', icon: FiGrid },
  { id: 'content', label: 'توضیحات و ترجمه', icon: FiFileText },
  { id: 'media', label: 'تصاویر و فایل', icon: FiImage },
  { id: 'release', label: 'انتشار و SEO', icon: FiSearch },
  { id: 'changelog', label: 'تغییرات نسخه', icon: FiPackage },
  { id: 'faq', label: 'سوالات متداول', icon: FiMessageSquare },
];
const ARTICLE_EDITOR_TABS = [
  { id: 'overview', label: 'اطلاعات پایه', icon: FiGrid },
  { id: 'content', label: 'متن و ترجمه', icon: FiFileText },
  { id: 'seo', label: 'SEO و انتشار', icon: FiSearch },
  { id: 'media', label: 'تصویر شاخص', icon: FiImage },
];
const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
};
const resolveMediaUrl = (value) => (value && value.startsWith('/') ? `${API_URL}${value}` : value);
const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (!size) return '۰ بایت';
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024).toLocaleString('fa-IR')} کیلوبایت`;
  return `${(size / (1024 * 1024)).toLocaleString('fa-IR', { maximumFractionDigits: 1 })} مگابایت`;
};

const AdminEntityEditorTabs = ({ tabs, activeTab, onChange, label, idPrefix }) => {
  const selectTab = (event, tabId) => {
    onChange(tabId);
    const scrollContainer = event.currentTarget.closest('.admin-form-content');
    const prefersReducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.requestAnimationFrame?.(() => {
      scrollContainer?.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });
  };

  const handleTabKeyDown = (event, currentIndex) => {
    const direction = event.key === 'ArrowLeft' ? 1 : event.key === 'ArrowRight' ? -1 : 0;
    if (!direction) return;
    event.preventDefault();
    const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
    const nextTab = tabs[nextIndex];
    selectTab(event, nextTab.id);
    window.requestAnimationFrame?.(() => {
      document.getElementById(`${idPrefix}-${nextTab.id}-tab`)?.focus();
    });
  };

  return (
    <nav className="admin-editor-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab, index) => {
        const TabIcon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            type="button"
            role="tab"
            id={`${idPrefix}-${tab.id}-tab`}
            aria-selected={isActive}
            aria-controls={`${idPrefix}-${tab.id}-panel`}
            tabIndex={isActive ? 0 : -1}
            className={isActive ? 'active' : ''}
            onClick={(event) => selectTab(event, tab.id)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
            key={tab.id}
          >
            <TabIcon aria-hidden="true" />
            <span>{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

const AdminEditorPanelHeading = ({ title, description }) => (
  <div className="admin-editor-panel-heading">
    <h4>{title}</h4>
    <p>{description}</p>
  </div>
);

const AdminFormModal = ({ title, eyebrow, description, icon, onClose, children }) => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div className="admin-form-modal" role="dialog" aria-modal="true" aria-labelledby="admin-form-title" dir="rtl">
      <div className="admin-form-shell">
        <header className="admin-form-header">
          <div className="admin-form-heading">
            <span className="admin-form-icon" aria-hidden="true">{icon}</span>
            <div>
              <span className="admin-form-eyebrow">{eyebrow}</span>
              <h3 id="admin-form-title">{title}</h3>
              <p>{description}</p>
            </div>
          </div>
          <button type="button" className="admin-form-close ui-close-button" onClick={onClose} aria-label="بستن فرم">
            <FiX aria-hidden="true" />
          </button>
        </header>
        <main className="admin-form-content">{children}</main>
      </div>
    </div>,
    document.body
  );
};

const AdminPanel = () => {
  const { settings: siteSettings } = useSiteSettings();
  const [activeTab, setActiveTab] = useState(() => {
    const requested = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('section')
      : '';
    return ADMIN_TABS.some((tab) => tab.id === requested) ? requested : 'dashboard';
  });
  const [users, setUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'user',
    avatar: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productFormData, setProductFormData] = useState(() => emptyProductForm('قالب وردپرس'));
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productCategories, setProductCategories] = useState([]);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [productImageUploading, setProductImageUploading] = useState('');
  const [productImageError, setProductImageError] = useState({ cardImageUrl: '', detailImageUrl: '' });
  const [productFileUploading, setProductFileUploading] = useState(false);
  const [productFileError, setProductFileError] = useState('');
  const [showProductDescriptionTranslations, setShowProductDescriptionTranslations] = useState(false);
  const [productEditorTab, setProductEditorTab] = useState('overview');
  const [articles, setArticles] = useState([]);
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleFormData, setArticleFormData] = useState(() => emptyArticleForm('آموزشی'));
  const [articleSearchTerm, setArticleSearchTerm] = useState('');
  const [articleCategories, setArticleCategories] = useState([]);
  const [showArticleCategoryManager, setShowArticleCategoryManager] = useState(false);
  const [newArticleCategoryName, setNewArticleCategoryName] = useState('');
  const [editingArticleCategoryId, setEditingArticleCategoryId] = useState(null);
  const [editingArticleCategoryName, setEditingArticleCategoryName] = useState('');
  const [articleCategoryBusy, setArticleCategoryBusy] = useState(false);
  const [articleCategoryError, setArticleCategoryError] = useState('');
  const [articleImageUploading, setArticleImageUploading] = useState(false);
  const [articleImageError, setArticleImageError] = useState('');
  const [showArticleContentTranslations, setShowArticleContentTranslations] = useState(false);
  const [articleEditorTab, setArticleEditorTab] = useState('overview');
  const [orders, setOrders] = useState([]);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderFormData, setOrderFormData] = useState(emptyOrderForm());
  const [manualOrderPayment, setManualOrderPayment] = useState(emptyManualOrderPayment());
  const [orderPaymentBusy, setOrderPaymentBusy] = useState(false);
  const [orderPaymentError, setOrderPaymentError] = useState('');
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [serviceOrderCount, setServiceOrderCount] = useState(0);
  const [transactionCount, setTransactionCount] = useState(0);
  const [serviceOrderToOpen, setServiceOrderToOpen] = useState('');
  const [contactMessageCount, setContactMessageCount] = useState(0);
  const [productReviewCount, setProductReviewCount] = useState(0);
  const [services, setServices] = useState([]);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceFormData, setServiceFormData] = useState(() => createEmptyService());
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    siteTitle: 'هریاد',
    logoUrl: '',
    primaryDark: '#03045E',
    primary: '#0077B6',
    primaryLight: '#00B4D8',
    secondary: '#90E0EF',
    secondaryLight: '#CAF0F8'
  });

  const closeUserForm = useCallback(() => {
    setShowUserForm(false);
    setEditingUser(null);
    setUserFormData({ name: '', email: '', phone: '', password: '', role: 'user', avatar: '' });
  }, []);

  const closeServiceForm = useCallback(() => {
    setShowServiceForm(false);
    setEditingService(null);
    setServiceFormData(createEmptyService());
  }, []);

  const closeProductForm = useCallback(() => {
    setShowProductForm(false);
    setEditingProduct(null);
    setProductFormData(emptyProductForm());
    setShowCategoryManager(false);
    setNewCategoryName('');
    setEditingCategoryId(null);
    setEditingCategoryName('');
    setCategoryError('');
    setProductImageUploading('');
    setProductImageError({ cardImageUrl: '', detailImageUrl: '' });
    setProductFileError('');
    setShowProductDescriptionTranslations(false);
    setProductEditorTab('overview');
  }, []);

  const closeArticleForm = useCallback(() => {
    setShowArticleForm(false);
    setEditingArticle(null);
    setArticleFormData(emptyArticleForm());
    setShowArticleCategoryManager(false);
    setNewArticleCategoryName('');
    setEditingArticleCategoryId(null);
    setEditingArticleCategoryName('');
    setArticleCategoryError('');
    setArticleImageUploading(false);
    setArticleImageError('');
    setShowArticleContentTranslations(false);
    setArticleEditorTab('overview');
  }, []);

  const closeOrderForm = useCallback(() => {
    setShowOrderForm(false);
    setEditingOrder(null);
    setOrderFormData(emptyOrderForm());
    setManualOrderPayment(emptyManualOrderPayment());
    setOrderPaymentError('');
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/api/users');
      if (res.success && Array.isArray(res.data)) setUsers(res.data.map(norm));
    } catch (_) {}
  }, []);
  const fetchProducts = useCallback(async () => {
    try {
      const res = await api.get('/api/products/admin/list');
      if (res.success && Array.isArray(res.data)) setProducts(res.data.map(norm));
    } catch (_) {}
  }, []);
  const fetchProductCategories = useCallback(async () => {
    try {
      const res = await api.get('/api/product-categories');
      if (res.success && Array.isArray(res.data)) {
        const categories = res.data.map(norm);
        setProductCategories(categories);
        setProductFormData((current) => ({
          ...current,
          category: current.category || categories[0]?.name || '',
        }));
      }
    } catch (_) {}
  }, []);
  const fetchArticles = useCallback(async () => {
    try {
      const res = await api.get('/api/articles/admin/list');
      if (res.success && Array.isArray(res.data)) setArticles(res.data.map(norm));
    } catch (_) {}
  }, []);
  const fetchArticleCategories = useCallback(async () => {
    try {
      const res = await api.get('/api/article-categories');
      if (res.success && Array.isArray(res.data)) {
        const categories = res.data.map(norm);
        setArticleCategories(categories);
        setArticleFormData((current) => ({
          ...current,
          category: current.category || categories[0]?.name || '',
        }));
      }
    } catch (_) {}
  }, []);
  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get('/api/orders');
      if (res.success && Array.isArray(res.data)) setOrders(res.data.map(norm));
    } catch (_) {}
  }, []);
  const fetchServices = useCallback(async () => {
    try {
      const res = await api.get('/api/services/admin/list');
      if (res.success && Array.isArray(res.data)) setServices(hydrateServices(res.data).map(norm));
    } catch (_) {}
  }, []);
  const fetchServiceOrderCount = useCallback(async () => {
    try {
      const res = await api.get('/api/service-orders/admin/list');
      setServiceOrderCount(Array.isArray(res.data) ? res.data.length : 0);
    } catch (_) {}
  }, []);
  const fetchTransactionCount = useCallback(async () => {
    try {
      const res = await api.get('/api/payment/admin/transactions?page=1&limit=10');
      setTransactionCount(Number(res.totals?.total || 0));
    } catch (_) {}
  }, []);
  const fetchContactMessageCount = useCallback(async () => {
    try {
      const res = await api.get('/api/contact-messages?limit=10');
      setContactMessageCount(Number(res.data?.unreadCount || 0));
    } catch (_) {}
  }, []);
  const fetchProductReviewCount = useCallback(async () => {
    try {
      const res = await api.get('/api/product-reviews/admin/list?page=1&limit=1');
      setProductReviewCount(Number(res.data?.counts?.pending || 0));
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchProducts();
    fetchProductCategories();
    fetchArticles();
    fetchArticleCategories();
    fetchOrders();
    fetchServices();
    fetchServiceOrderCount();
    fetchTransactionCount();
    fetchContactMessageCount();
    fetchProductReviewCount();
  }, [fetchUsers, fetchProducts, fetchProductCategories, fetchArticles, fetchArticleCategories, fetchOrders, fetchServices, fetchServiceOrderCount, fetchTransactionCount, fetchContactMessageCount, fetchProductReviewCount]);

  const stats = [
    {
      label: 'کل کاربران',
      value: users.length.toLocaleString('fa-IR'),
      icon: <FiUsers />,
      tone: 'blue',
      helper: 'حساب ثبت‌شده',
    },
    {
      label: 'کل سفارشات',
      value: orders.length.toLocaleString('fa-IR'),
      icon: <FiShoppingBag />,
      tone: 'cyan',
      helper: 'سفارش در سیستم',
    },
    {
      label: 'در انتظار بررسی',
      value: orders.filter((order) => order.status === 'در انتظار پرداخت').length.toLocaleString('fa-IR'),
      icon: <FiActivity />,
      tone: 'amber',
      helper: 'نیازمند اقدام',
    },
    {
      label: 'محتوای منتشرشده',
      value: articles.length.toLocaleString('fa-IR'),
      icon: <FiFileText />,
      tone: 'violet',
      helper: 'مقاله ثبت‌شده',
    },
  ];

  const tabCounts = {
    users: users.length,
    services: services.length,
    products: products.length,
    articles: articles.length,
    orders: orders.length,
    'service-orders': serviceOrderCount,
    finance: transactionCount,
    reviews: productReviewCount,
    'contact-messages': contactMessageCount,
  };
  const activeTabInfo = ADMIN_TABS.find((tab) => tab.id === activeTab) || ADMIN_TABS[0];
  const ActiveTabIcon = activeTabInfo.icon;
  const todayLabel = new Intl.DateTimeFormat('fa-IR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date());

  const recentOrders = orders.slice(0, 4); // Get latest 4 orders

  // User management functions
  const handleUserFormChange = (e) => {
    setUserFormData({
      ...userFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setUserFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      role: 'user',
      avatar: ''
    });
    setShowUserForm(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      password: '',
      role: user.role || 'user',
      avatar: user.avatar || ''
    });
    setShowUserForm(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('آیا از حذف این کاربر اطمینان دارید؟')) return;
    try {
      await api.delete(`/api/users/${userId}`);
      fetchUsers();
    } catch (err) {
      alert(err.data?.message || err.message || 'خطا در حذف');
    }
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    if (!userFormData.name || !userFormData.email) {
      alert('لطفا نام و ایمیل را وارد کنید');
      return;
    }
    try {
      if (editingUser) {
        const body = { name: userFormData.name, email: userFormData.email, phone: userFormData.phone, role: userFormData.role, avatar: userFormData.avatar || '' };
        if (userFormData.password) body.password = userFormData.password;
        await api.put(`/api/users/${editingUser.id || editingUser._id}`, body);
      } else {
        await api.post('/api/users', {
          name: userFormData.name,
          email: userFormData.email,
          phone: userFormData.phone,
          password: userFormData.password || '123456',
          role: userFormData.role,
          avatar: userFormData.avatar || '',
        });
      }
      setShowUserForm(false);
      setEditingUser(null);
      setUserFormData({ name: '', email: '', phone: '', password: '', role: 'user', avatar: '' });
      fetchUsers();
    } catch (err) {
      alert(err.data?.message || err.message || 'خطا در ذخیره');
    }
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.phone || '').includes(searchTerm)
  );

  // Product management functions
  const handleProductFormChange = (e) => {
    setProductFormData((current) => ({
      ...current,
      [e.target.name]: e.target.value
    }));
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductFormData(emptyProductForm(productCategories[0]?.name || ''));
    setProductImageError({ cardImageUrl: '', detailImageUrl: '' });
    setProductFileError('');
    setShowProductDescriptionTranslations(false);
    setProductEditorTab('overview');
    setShowProductForm(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductFormData({
      name: product.name,
      slug: product.slug || '',
      description: product.description,
      price: typeof product.price === 'number' ? product.price : (product.price || ''),
      discountPrice: typeof product.discountPrice === 'number' ? product.discountPrice : (product.discountPrice || ''),
      cardImageUrl: product.cardImageUrl || product.imageUrl || '',
      detailImageUrl: product.detailImageUrl || product.imageUrl || '',
      features: Array.isArray(product.features) ? product.features.join('\n') : (product.features || ''),
      category: product.category || productCategories[0]?.name || '',
      seoTitle: product.seoTitle || '',
      seoDescription: product.seoDescription || '',
      version: product.version || '1.0.0',
      releaseDate: toDateInputValue(product.releaseDate),
      lastUpdated: toDateInputValue(product.lastUpdated),
      changelog: Array.isArray(product.changelog)
        ? product.changelog.map((entry) => ({
            sourceId: entry.sourceId || '',
            version: entry.version || '',
            date: toDateInputValue(entry.date),
            items: Array.isArray(entry.items) ? entry.items.join('\n') : (entry.items || ''),
          }))
        : [],
      faq: Array.isArray(product.faq)
        ? product.faq.map((item) => ({ sourceId: item.sourceId || '', q: item.q || '', a: item.a || '' }))
        : [],
    });
    setProductImageError({ cardImageUrl: '', detailImageUrl: '' });
    setProductFileError('');
    setShowProductDescriptionTranslations(false);
    setProductEditorTab('overview');
    setShowProductForm(true);
  };

  const handleProductImageUpload = async (event, fieldName) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
    if (!allowedTypes.includes(file.type)) {
      setProductImageError((current) => ({
        ...current,
        [fieldName]: 'فرمت تصویر باید JPG، PNG، WEBP، GIF یا AVIF باشد.',
      }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setProductImageError((current) => ({
        ...current,
        [fieldName]: 'حجم تصویر نباید بیشتر از ۵ مگابایت باشد.',
      }));
      return;
    }

    setProductImageUploading(fieldName);
    setProductImageError((current) => ({ ...current, [fieldName]: '' }));
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.upload('/api/products/upload-image', formData);
      if (res.success && res.data?.imageUrl) {
        setProductFormData((current) => ({ ...current, [fieldName]: res.data.imageUrl }));
      }
    } catch (err) {
      setProductImageError((current) => ({
        ...current,
        [fieldName]: err.data?.message || err.message || 'آپلود تصویر انجام نشد',
      }));
    } finally {
      setProductImageUploading('');
    }
  };

  const handleProductDescriptionImageUpload = async (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('فرمت تصویر باید JPG، PNG، WEBP، GIF یا AVIF باشد.');
    }
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('حجم تصویر نباید بیشتر از ۵ مگابایت باشد.');
    }

    const formData = new FormData();
    formData.append('image', file);
    const res = await api.upload('/api/products/upload-image', formData);
    if (!res.success || !res.data?.imageUrl) {
      throw new Error('آپلود تصویر انجام نشد.');
    }
    return resolveMediaUrl(res.data.imageUrl);
  };

  const handleProductFileUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !editingProduct) return;

    if (!file.name.toLowerCase().endsWith('.zip')) {
      setProductFileError('فقط فایل ZIP قابل آپلود است.');
      return;
    }
    const maxProductFileSizeMb = Number(siteSettings.store.maxProductFileSizeMb || 200);
    if (file.size > maxProductFileSizeMb * 1024 * 1024) {
      setProductFileError(`حجم فایل محصول نباید بیشتر از ${maxProductFileSizeMb.toLocaleString('fa-IR')} مگابایت باشد.`);
      return;
    }

    setProductFileUploading(true);
    setProductFileError('');
    try {
      const formData = new FormData();
      formData.append('productFile', file);
      const productId = editingProduct.id || editingProduct._id;
      const res = await api.upload(`/api/products/${productId}/upload-file`, formData);
      if (res.success && res.data?.downloadFile) {
        const downloadFile = res.data.downloadFile;
        setEditingProduct((current) => ({ ...current, downloadFile }));
        setProducts((current) => current.map((product) => (
          (product.id || product._id) === productId ? { ...product, downloadFile } : product
        )));
      }
    } catch (err) {
      setProductFileError(err.data?.message || err.message || 'آپلود فایل محصول انجام نشد');
    } finally {
      setProductFileUploading(false);
    }
  };

  const handleDeleteProductFile = async () => {
    if (!editingProduct || !window.confirm('فایل دانلود فعلی محصول حذف شود؟')) return;
    const productId = editingProduct.id || editingProduct._id;
    setProductFileUploading(true);
    setProductFileError('');
    try {
      await api.delete(`/api/products/${productId}/file`);
      const downloadFile = null;
      setEditingProduct((current) => ({ ...current, downloadFile }));
      setProducts((current) => current.map((product) => (
        (product.id || product._id) === productId ? { ...product, downloadFile } : product
      )));
    } catch (err) {
      setProductFileError(err.data?.message || err.message || 'حذف فایل محصول انجام نشد');
    } finally {
      setProductFileUploading(false);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      setCategoryError('نام دسته‌بندی را وارد کنید.');
      return;
    }
    setCategoryBusy(true);
    setCategoryError('');
    try {
      const res = await api.post('/api/product-categories', { name });
      setNewCategoryName('');
      if (res.data?.name) {
        setProductFormData((current) => ({ ...current, category: res.data.name }));
      }
      await fetchProductCategories();
    } catch (err) {
      setCategoryError(err.data?.message || err.message || 'افزودن دسته‌بندی انجام نشد');
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleUpdateCategory = async (category) => {
    const name = editingCategoryName.trim();
    if (!name) {
      setCategoryError('نام دسته‌بندی را وارد کنید.');
      return;
    }
    setCategoryBusy(true);
    setCategoryError('');
    try {
      const res = await api.put(`/api/product-categories/${category.id || category._id}`, { name });
      if (productFormData.category === category.name && res.data?.name) {
        setProductFormData((current) => ({ ...current, category: res.data.name }));
      }
      setEditingCategoryId(null);
      setEditingCategoryName('');
      await Promise.all([fetchProductCategories(), fetchProducts()]);
    } catch (err) {
      setCategoryError(err.data?.message || err.message || 'ویرایش دسته‌بندی انجام نشد');
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!window.confirm(`دسته‌بندی «${category.name}» حذف شود؟`)) return;
    setCategoryBusy(true);
    setCategoryError('');
    try {
      await api.delete(`/api/product-categories/${category.id || category._id}`);
      const remaining = productCategories.filter((item) => (item.id || item._id) !== (category.id || category._id));
      if (productFormData.category === category.name) {
        setProductFormData((current) => ({ ...current, category: remaining[0]?.name || '' }));
      }
      await fetchProductCategories();
    } catch (err) {
      setCategoryError(err.data?.message || err.message || 'حذف دسته‌بندی انجام نشد');
    } finally {
      setCategoryBusy(false);
    }
  };

  const addProductFaq = () => {
    setProductFormData((current) => ({ ...current, faq: [...current.faq, { q: '', a: '' }] }));
  };

  const updateProductFaq = (index, key, value) => {
    setProductFormData((current) => ({
      ...current,
      faq: current.faq.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item),
    }));
  };

  const removeProductFaq = (index) => {
    setProductFormData((current) => ({ ...current, faq: current.faq.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const addProductChangelog = () => {
    setProductFormData((current) => ({
      ...current,
      changelog: [...current.changelog, { version: current.version || '', date: current.lastUpdated || '', items: '' }],
    }));
  };

  const updateProductChangelog = (index, key, value) => {
    setProductFormData((current) => ({
      ...current,
      changelog: current.changelog.map((entry, entryIndex) => entryIndex === index ? { ...entry, [key]: value } : entry),
    }));
  };

  const removeProductChangelog = (index) => {
    setProductFormData((current) => ({
      ...current,
      changelog: current.changelog.filter((_, entryIndex) => entryIndex !== index),
    }));
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('آیا از حذف این محصول اطمینان دارید؟')) return;
    try {
      await api.delete(`/api/products/${productId}`);
      fetchProducts();
    } catch (err) {
      alert(err.data?.message || err.message || 'خطا در حذف');
    }
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    if (!productFormData.name || !productFormData.category || !productFormData.price) {
      setProductEditorTab('overview');
      alert('لطفا تمام فیلدهای الزامی را پر کنید');
      return;
    }
    if (!hasRichTextContent(productFormData.description)) {
      setProductEditorTab('content');
      alert('لطفا توضیحات کامل محصول را وارد کنید');
      return;
    }
    const featuresArray = (productFormData.features || '')
      .split('\n')
      .map((f) => f.trim())
      .filter((f) => f.length > 0);
    const priceNum = parseInt(String(productFormData.price).replace(/,/g, ''), 10) || 0;
    const discountPriceNum = productFormData.discountPrice === ''
      ? null
      : parseInt(String(productFormData.discountPrice).replace(/,/g, ''), 10) || 0;
    if (discountPriceNum !== null && (discountPriceNum <= 0 || discountPriceNum >= priceNum)) {
      setProductEditorTab('overview');
      alert('قیمت تخفیفی باید بیشتر از صفر و کمتر از قیمت فروش باشد');
      return;
    }
    const body = {
      name: productFormData.name,
      slug: productFormData.slug || '',
      description: productFormData.description,
      price: priceNum,
      discountPrice: discountPriceNum,
      imageUrl: '',
      cardImageUrl: productFormData.cardImageUrl || '',
      detailImageUrl: productFormData.detailImageUrl || '',
      features: featuresArray,
      category: productFormData.category || '',
      seoTitle: productFormData.seoTitle || '',
      seoDescription: productFormData.seoDescription || '',
      version: productFormData.version || '1.0.0',
      releaseDate: productFormData.releaseDate || null,
      lastUpdated: productFormData.lastUpdated || null,
      changelog: productFormData.changelog.map((entry) => ({
        sourceId: entry.sourceId || undefined,
        version: entry.version.trim(),
        date: entry.date || null,
        items: (entry.items || '').split('\n').map((item) => item.trim()).filter(Boolean),
      })),
      faq: productFormData.faq.map((item) => ({ sourceId: item.sourceId || undefined, q: item.q.trim(), a: item.a.trim() })),
    };
    try {
      if (editingProduct) {
        await api.put(`/api/products/${editingProduct.id || editingProduct._id}`, body);
      } else {
        await api.post('/api/products', body);
      }
      closeProductForm();
      fetchProducts();
    } catch (err) {
      alert(err.data?.message || err.message || 'خطا در ذخیره');
    }
  };

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    richTextToPlainText(product.description).toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  // Article management functions
  const handleArticleFormChange = (e) => {
    setArticleFormData((current) => ({
      ...current,
      [e.target.name]: e.target.value
    }));
  };

  const handleAddArticle = () => {
    setEditingArticle(null);
    setArticleFormData(emptyArticleForm(articleCategories[0]?.name || ''));
    setArticleImageError('');
    setShowArticleContentTranslations(false);
    setArticleEditorTab('overview');
    setShowArticleForm(true);
  };

  const handleEditArticle = (article) => {
    setEditingArticle(article);
    setArticleFormData({
      title: article.title || '',
      slug: article.slug || '',
      excerpt: article.excerpt || '',
      summary: article.summary || '',
      content: article.content || '',
      category: article.category || articleCategories[0]?.name || '',
      imageUrl: article.imageUrl || '',
      imageAlt: article.imageAlt || '',
      seoTitle: article.seoTitle || '',
      seoDescription: article.seoDescription || '',
      primaryKeyword: article.primaryKeyword || '',
      secondaryKeywords: Array.isArray(article.secondaryKeywords) ? article.secondaryKeywords : [],
      faq: Array.isArray(article.faq) ? article.faq.map((item) => ({
        sourceId: item.sourceId || '',
        question: item.question || item.q || '',
        answer: item.answer || item.a || '',
      })) : [],
      internalLinks: Array.isArray(article.internalLinks) ? article.internalLinks : [],
      relatedArticles: Array.isArray(article.relatedArticles) ? article.relatedArticles.map((item) => item?._id || item?.id || item) : [],
      schemaTypes: Array.isArray(article.schemaTypes) && article.schemaTypes.length ? article.schemaTypes : ['Article', 'BreadcrumbList'],
      cta: {
        title: article.cta?.title || '',
        text: article.cta?.text || '',
        label: article.cta?.label || '',
        url: article.cta?.url || '',
      },
      publicationStatus: article.publicationStatus === 'draft' ? 'draft' : 'published',
    });
    setArticleImageError('');
    setShowArticleContentTranslations(false);
    setArticleEditorTab('overview');
    setShowArticleForm(true);
  };

  const handleUseAiPackage = (packageArticle) => {
    if (!packageArticle) return;
    setEditingArticle(null);
    setArticleFormData({
      ...emptyArticleForm(packageArticle.category || articleCategories[0]?.name || ''),
      title: packageArticle.title || '',
      slug: packageArticle.slug || '',
      excerpt: packageArticle.excerpt || '',
      summary: packageArticle.summary || '',
      content: packageArticle.contentHtml || packageArticle.content || '',
      category: packageArticle.category || articleCategories[0]?.name || '',
      imageAlt: packageArticle.imageAlt || '',
      seoTitle: packageArticle.seoTitle || '',
      seoDescription: packageArticle.metaDescription || packageArticle.seoDescription || '',
      primaryKeyword: packageArticle.primaryKeyword || '',
      secondaryKeywords: Array.isArray(packageArticle.secondaryKeywords) ? packageArticle.secondaryKeywords : [],
      faq: Array.isArray(packageArticle.faq) ? packageArticle.faq.map((item) => ({
        sourceId: item.sourceId || '',
        question: item.question || item.q || '',
        answer: item.answer || item.a || '',
      })) : [],
      internalLinks: Array.isArray(packageArticle.internalLinks) ? packageArticle.internalLinks.map((item) => ({
        article: item.articleId || item.article || '',
        anchorText: item.anchorText || '',
        reason: item.reason || '',
      })) : [],
      relatedArticles: Array.isArray(packageArticle.relatedArticleIds) ? packageArticle.relatedArticleIds : [],
      schemaTypes: Array.isArray(packageArticle.schemaTypes) && packageArticle.schemaTypes.length ? packageArticle.schemaTypes : ['Article', 'BreadcrumbList'],
      cta: {
        title: packageArticle.cta?.title || '',
        text: packageArticle.cta?.text || '',
        label: packageArticle.cta?.label || '',
        url: packageArticle.cta?.url || '',
      },
      publicationStatus: 'draft',
    });
    setArticleImageError('');
    setShowArticleContentTranslations(false);
    setArticleEditorTab('overview');
    setShowArticleForm(true);
    setActiveTab('articles');
  };

  const validateArticleImage = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
    if (!allowedTypes.includes(file.type)) return 'فرمت تصویر باید JPG، PNG، WEBP، GIF یا AVIF باشد.';
    if (file.size > 5 * 1024 * 1024) return 'حجم تصویر نباید بیشتر از ۵ مگابایت باشد.';
    return '';
  };

  const uploadArticleImageFile = async (file) => {
    const validationError = validateArticleImage(file);
    if (validationError) throw new Error(validationError);

    const formData = new FormData();
    formData.append('image', file);
    const res = await api.upload('/api/articles/upload-image', formData);
    if (!res.success || !res.data?.imageUrl) throw new Error('آپلود تصویر انجام نشد.');
    return res.data.imageUrl;
  };

  const handleArticleFeaturedImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setArticleImageUploading(true);
    setArticleImageError('');
    try {
      const imageUrl = await uploadArticleImageFile(file);
      setArticleFormData((current) => ({ ...current, imageUrl }));
    } catch (error) {
      setArticleImageError(error.message || 'آپلود تصویر انجام نشد');
    } finally {
      setArticleImageUploading(false);
    }
  };

  const handleArticleContentImageUpload = async (file) => {
    const imageUrl = await uploadArticleImageFile(file);
    return resolveMediaUrl(imageUrl);
  };

  const handleArticleContentVideoUpload = async (file) => {
    const formData = new FormData();
    formData.append('video', file);
    const res = await api.upload('/api/articles/upload-video', formData);
    if (!res.success || !res.data?.videoUrl) throw new Error('آپلود ویدیو انجام نشد.');
    return resolveMediaUrl(res.data.videoUrl);
  };

  const handleCreateArticleCategory = async () => {
    const name = newArticleCategoryName.trim();
    if (!name) {
      setArticleCategoryError('نام دسته‌بندی را وارد کنید.');
      return;
    }
    setArticleCategoryBusy(true);
    setArticleCategoryError('');
    try {
      const res = await api.post('/api/article-categories', { name });
      setNewArticleCategoryName('');
      if (res.data?.name) {
        setArticleFormData((current) => ({ ...current, category: res.data.name }));
      }
      await fetchArticleCategories();
    } catch (err) {
      setArticleCategoryError(err.data?.message || err.message || 'افزودن دسته‌بندی انجام نشد');
    } finally {
      setArticleCategoryBusy(false);
    }
  };

  const handleUpdateArticleCategory = async (category) => {
    const name = editingArticleCategoryName.trim();
    if (!name) {
      setArticleCategoryError('نام دسته‌بندی را وارد کنید.');
      return;
    }
    setArticleCategoryBusy(true);
    setArticleCategoryError('');
    try {
      const res = await api.put(`/api/article-categories/${category.id || category._id}`, { name });
      if (articleFormData.category === category.name && res.data?.name) {
        setArticleFormData((current) => ({ ...current, category: res.data.name }));
      }
      setEditingArticleCategoryId(null);
      setEditingArticleCategoryName('');
      await Promise.all([fetchArticleCategories(), fetchArticles()]);
    } catch (err) {
      setArticleCategoryError(err.data?.message || err.message || 'ویرایش دسته‌بندی انجام نشد');
    } finally {
      setArticleCategoryBusy(false);
    }
  };

  const handleDeleteArticleCategory = async (category) => {
    if (!window.confirm(`دسته‌بندی «${category.name}» حذف شود؟`)) return;
    setArticleCategoryBusy(true);
    setArticleCategoryError('');
    try {
      await api.delete(`/api/article-categories/${category.id || category._id}`);
      const remaining = articleCategories.filter((item) => (item.id || item._id) !== (category.id || category._id));
      if (articleFormData.category === category.name) {
        setArticleFormData((current) => ({ ...current, category: remaining[0]?.name || '' }));
      }
      await fetchArticleCategories();
    } catch (err) {
      setArticleCategoryError(err.data?.message || err.message || 'حذف دسته‌بندی انجام نشد');
    } finally {
      setArticleCategoryBusy(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('آیا از حذف این مقاله اطمینان دارید؟')) return;
    try {
      await api.delete(`/api/articles/${articleId}`);
      fetchArticles();
    } catch (err) {
      alert(err.data?.message || err.message || 'خطا در حذف');
    }
  };

  const handleSubmitArticle = async (e) => {
    e.preventDefault();
    if (!articleFormData.title || !articleFormData.category || !articleFormData.excerpt) {
      setArticleEditorTab('overview');
      alert('لطفا عنوان، دسته‌بندی و خلاصه مقاله را وارد کنید');
      return;
    }
    if (!hasRichTextContent(articleFormData.content, { allowVideo: true })) {
      setArticleEditorTab('content');
      alert('لطفا متن کامل مقاله را وارد کنید');
      return;
    }
    const body = {
      title: articleFormData.title,
      slug: articleFormData.slug || '',
      excerpt: articleFormData.excerpt,
      summary: articleFormData.summary || '',
      content: articleFormData.content || '',
      category: articleFormData.category || articleCategories[0]?.name || 'آموزشی',
      imageUrl: articleFormData.imageUrl || '',
      imageAlt: articleFormData.imageAlt || '',
      seoTitle: articleFormData.seoTitle || '',
      seoDescription: articleFormData.seoDescription || '',
      primaryKeyword: articleFormData.primaryKeyword || '',
      secondaryKeywords: Array.isArray(articleFormData.secondaryKeywords) ? articleFormData.secondaryKeywords : [],
      faq: Array.isArray(articleFormData.faq) ? articleFormData.faq.filter((item) => item.question?.trim() && item.answer?.trim()) : [],
      internalLinks: Array.isArray(articleFormData.internalLinks) ? articleFormData.internalLinks : [],
      relatedArticles: Array.isArray(articleFormData.relatedArticles) ? articleFormData.relatedArticles : [],
      schemaTypes: Array.isArray(articleFormData.schemaTypes) ? articleFormData.schemaTypes : [],
      cta: articleFormData.cta || { title: '', text: '', label: '', url: '' },
      publicationStatus: articleFormData.publicationStatus === 'draft' ? 'draft' : 'published',
    };
    try {
      if (editingArticle) {
        await api.put(`/api/articles/${editingArticle.id || editingArticle._id}`, body);
      } else {
        await api.post('/api/articles', body);
      }
      closeArticleForm();
      fetchArticles();
    } catch (err) {
      alert(err.data?.message || err.message || 'خطا در ذخیره');
    }
  };

  const filteredArticles = articles.filter(article => 
    article.title.toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
    String(article.excerpt || '').toLowerCase().includes(articleSearchTerm.toLowerCase()) ||
    String(article.category || '').toLowerCase().includes(articleSearchTerm.toLowerCase())
  );

  // Order management functions
  const handleOrderFormChange = (e) => {
    setOrderFormData({
      ...orderFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleAddOrder = () => {
    setEditingOrder(null);
    setOrderFormData(emptyOrderForm());
    setManualOrderPayment(emptyManualOrderPayment());
    setOrderPaymentError('');
    setShowOrderForm(true);
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    const customerId = order.customer?._id || order.customer;
    const productId = order.product?._id || order.product;
    setOrderFormData({
      customer: typeof order.customer === 'object' ? order.customer?.name : order.customer,
      product: typeof order.product === 'object' ? order.product?.name : order.product,
      customerId: customerId || '',
      productId: productId || '',
      amount: order.amount || '',
      status: order.status || 'در انتظار پرداخت'
    });
    setManualOrderPayment(emptyManualOrderPayment(order.remainingAmount || order.amount || ''));
    setOrderPaymentError('');
    setShowOrderForm(true);
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('آیا از حذف این سفارش اطمینان دارید؟')) return;
    try {
      await api.delete(`/api/orders/${orderId}`);
      fetchOrders();
    } catch (err) {
      alert(err.data?.message || err.message || 'خطا در حذف');
    }
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    const customerId = editingOrder ? (orderFormData.customerId || orderFormData.customer) : orderFormData.customerId;
    const productId = editingOrder ? (orderFormData.productId || orderFormData.product) : orderFormData.productId;
    const amount = parseInt(String(orderFormData.amount).replace(/,/g, ''), 10) || 0;
    if (!customerId || !productId || !amount) {
      alert('لطفا مشتری، محصول و مبلغ را انتخاب/وارد کنید');
      return;
    }
    try {
      if (editingOrder) {
        await api.put(`/api/orders/${editingOrder.id || editingOrder._id}`, {
          customer: customerId,
          product: productId,
          amount,
          status: orderFormData.status,
        });
      } else {
        await api.post('/api/orders', { customer: customerId, product: productId, amount, status: orderFormData.status });
      }
      setShowOrderForm(false);
      setEditingOrder(null);
      setOrderFormData(emptyOrderForm());
      fetchOrders();
    } catch (err) {
      alert(err.data?.message || err.message || 'خطا در ذخیره');
    }
  };

  const handleManualOrderPayment = async () => {
    if (!editingOrder) return;
    const amount = parseInt(String(manualOrderPayment.amount).replace(/[,٬،\s]/g, ''), 10) || 0;
    if (!(amount > 0)) {
      setOrderPaymentError('مبلغ پرداخت‌شده را وارد کنید.');
      return;
    }
    setOrderPaymentBusy(true);
    setOrderPaymentError('');
    try {
      const response = await api.post(
        `/api/orders/${editingOrder.id || editingOrder._id}/manual-payments`,
        { ...manualOrderPayment, amount }
      );
      const updated = norm(response.data);
      setEditingOrder(updated);
      setOrders((current) => current.map((order) => (
        (order.id || order._id) === (updated.id || updated._id) ? updated : order
      )));
      setOrderFormData((current) => ({ ...current, status: updated.status }));
      setManualOrderPayment(emptyManualOrderPayment(updated.remainingAmount || ''));
      alert(response.message);
    } catch (err) {
      setOrderPaymentError(err.data?.message || err.message || 'ثبت پرداخت دستی انجام نشد');
    } finally {
      setOrderPaymentBusy(false);
    }
  };

  const orderCustomerName = (o) => (o.customer && typeof o.customer === 'object' ? o.customer.name : o.customer) || '—';
  const orderProductName = (o) => (o.product && typeof o.product === 'object' ? o.product.name : o.product) || '—';
  const filteredOrders = orders.filter((order) => {
    const cust = orderCustomerName(order);
    const prod = orderProductName(order);
    const status = `${order.status || ''} ${order.paymentStatus || ''} ${order.orderNumber || ''}`;
    return cust.toLowerCase().includes(orderSearchTerm.toLowerCase()) ||
      prod.toLowerCase().includes(orderSearchTerm.toLowerCase()) ||
      status.toLowerCase().includes(orderSearchTerm.toLowerCase());
  });

  const handleOpenFinancialOrder = async (transaction) => {
    if (transaction.orderType === 'service') {
      setServiceOrderToOpen(transaction.orderId || '');
      setActiveTab('service-orders');
      return;
    }

    setActiveTab('orders');
    const localOrder = orders.find((order) => (
      String(order.id || order._id) === String(transaction.orderId)
    ));
    if (localOrder) {
      handleEditOrder(localOrder);
      return;
    }

    try {
      const response = await api.get(`/api/orders/${transaction.orderId}`);
      if (response.data) handleEditOrder(norm(response.data));
    } catch (error) {
      alert(error.data?.message || error.message || 'سفارش مرتبط یافت نشد');
    }
  };

  const clearServiceOrderToOpen = useCallback(() => {
    setServiceOrderToOpen('');
  }, []);

  // Service management functions
  const handleAddService = () => {
    setEditingService(null);
    setServiceFormData(createEmptyService(services.length + 1));
    setShowServiceForm(true);
  };

  const handleEditService = (service) => {
    const baseForm = hydrateService(service);
    setEditingService(service);
    setServiceFormData(baseForm);
    setShowServiceForm(true);
  };

  const handleServiceFormDataChange = (nextValue) => {
    setServiceFormData(nextValue);
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('آیا از حذف این خدمات اطمینان دارید؟')) return;
    try {
      await api.delete(`/api/services/${serviceId}`);
      fetchServices();
    } catch (err) {
      alert(err.data?.message || err.message || 'خطا در حذف');
    }
  };

  const handleSubmitService = async (e) => {
    e.preventDefault();
    if (!serviceFormData.title || !serviceFormData.description) {
      alert('لطفا عنوان و توضیحات را وارد کنید');
      return;
    }
    const body = serializeService(serviceFormData);
    try {
      if (editingService) {
        await api.put(`/api/services/${editingService.id || editingService._id}`, body);
      } else {
        await api.post('/api/services', body);
      }
      setShowServiceForm(false);
      setEditingService(null);
      setServiceFormData(createEmptyService());
      fetchServices();
    } catch (err) {
      alert(err.data?.message || err.message || 'خطا در ذخیره');
    }
  };

  const filteredServices = services.filter(service =>
    (service.title || '').toLowerCase().includes(serviceSearchTerm.toLowerCase()) ||
    (service.description || '').toLowerCase().includes(serviceSearchTerm.toLowerCase())
  );

  // Settings functions
  const handleSettingsChange = (e) => {
    setSettings({
      ...settings,
      [e.target.name]: e.target.value
    });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        alert('لطفا فقط فایل تصویر آپلود کنید');
        return;
      }
      
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert('حجم فایل نباید بیشتر از 2 مگابایت باشد');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings({
          ...settings,
          logoUrl: reader.result // This will be a base64 string
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveSettings = (e) => {
    e.preventDefault();
    alert('تنظیمات با موفقیت ذخیره شد!');
    setShowSettings(false);
  };

  return (
    <div className="admin-panel" dir="rtl">
      <div className="admin-workspace">
        <div className="admin-layout">
          <aside className="admin-sidebar">
            <div className="admin-sidebar-inner">
              <div className="admin-brand">
                <span className="admin-brand-mark" aria-hidden="true"><FiLayers /></span>
                <div>
                  <small>پنل مدیریت</small>
                  <strong>{siteSettings.general.siteTitle || 'هریاد'}</strong>
                </div>
              </div>

              <nav className="admin-tabs" aria-label="بخش‌های پنل مدیریت">
                {ADMIN_TABS.map((tab) => {
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                      aria-current={activeTab === tab.id ? 'page' : undefined}
                    >
                      <span className="tab-btn-icon" aria-hidden="true"><TabIcon /></span>
                      <span className="tab-btn-label">{tab.label}</span>
                      {!['dashboard', 'backups', 'settings'].includes(tab.id) && (
                        <span className="tab-btn-count">{Number(tabCounts[tab.id] || 0).toLocaleString('fa-IR')}</span>
                      )}
                    </button>
                  );
                })}
              </nav>

              <div className="admin-sidebar-status">
                <span aria-hidden="true"><FiCheckCircle /></span>
                <div>
                  <small>وضعیت پنل</small>
                  <strong>همه بخش‌ها فعال‌اند</strong>
                </div>
              </div>
            </div>
          </aside>

          <div className="admin-main-content">
            <header className="admin-topbar">
              <div className="admin-page-heading">
                <span className="admin-page-icon" aria-hidden="true"><ActiveTabIcon /></span>
                <div>
                  <span className="admin-breadcrumb">پنل مدیریت <FiChevronLeft /> {activeTabInfo.label}</span>
                  <h1>{activeTabInfo.label}</h1>
                  <p>{activeTabInfo.description}</p>
                </div>
              </div>
              <div className="admin-today-card">
                <span aria-hidden="true"><FiCalendar /></span>
                <div>
                  <small>امروز</small>
                  <strong>{todayLabel}</strong>
                </div>
              </div>
            </header>

            {activeTab === 'dashboard' && (
              <div key="dashboard" className="admin-content admin-dashboard">
                <section className="admin-welcome-card">
                  <div>
                    <span className="admin-welcome-eyebrow"><FiTrendingUp /> مرکز کنترل هریاد</span>
                    <h2>همه‌چیز برای مدیریت امروز آماده است.</h2>
                    <p>آمار اصلی را مرور کنید یا از میانبرها برای ثبت سریع محتوای جدید استفاده کنید.</p>
                  </div>
                  <button type="button" onClick={() => { setActiveTab('products'); setTimeout(() => handleAddProduct(), 100); }}>
                    <FiPlus /> افزودن محصول جدید
                  </button>
                </section>

                <div className="stats-grid">
                  {stats.map((stat) => (
                    <div key={stat.label} className={`stat-card stat-card-${stat.tone}`}>
                      <div className="stat-card-top">
                        <span className="stat-icon" aria-hidden="true">{stat.icon}</span>
                        <span className="stat-card-trend"><FiTrendingUp /> به‌روز</span>
                      </div>
                      <div className="stat-info">
                        <h3>{stat.value}</h3>
                        <p>{stat.label}</p>
                        <small>{stat.helper}</small>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="admin-sections">
                  <section className="section-panel admin-recent-orders">
                    <div className="panel-header-section">
                      <div className="panel-heading">
                        <span className="panel-heading-icon"><FiShoppingBag /></span>
                        <div>
                          <h2>سفارشات اخیر</h2>
                          <p>آخرین سفارش‌های ثبت‌شده در وب‌سایت</p>
                        </div>
                      </div>
                      <button type="button" className="panel-link-btn" onClick={() => setActiveTab('orders')}>
                        مشاهده همه <FiArrowRight />
                      </button>
                    </div>
                    <div className="table-container">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>شناسه</th>
                            <th>مشتری</th>
                            <th>محصول</th>
                            <th>مبلغ</th>
                            <th>وضعیت</th>
                            <th>عملیات</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recentOrders.length === 0 ? (
                            <tr><td colSpan="6" className="admin-empty-cell">هنوز سفارشی ثبت نشده است.</td></tr>
                          ) : recentOrders.map((order) => (
                            <tr key={order.id || order._id}>
                              <td>#{order.orderNumber || order.id || order._id}</td>
                              <td>{orderCustomerName(order)}</td>
                              <td>{orderProductName(order)}</td>
                              <td>{(typeof order.amount === 'number' ? order.amount.toLocaleString('fa-IR') : order.amount) || '—'} {order.currencyLabel || 'تومان'}</td>
                              <td>
                                <span className={`status-badge status-${(order.status || '').replace(/ /g, '-')}`}>
                                  {order.status || '—'}
                                </span>
                              </td>
                              <td>
                                <button type="button" className="action-btn" onClick={() => { setActiveTab('orders'); handleEditOrder(order); }}>
                                  مشاهده <FiArrowRight />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  <section className="quick-actions">
                    <div className="quick-actions-heading">
                      <span><FiActivity /></span>
                      <div>
                        <h2>عملیات سریع</h2>
                        <p>دسترسی کوتاه به کارهای پرتکرار</p>
                      </div>
                    </div>
                    <div className="actions-grid">
                      <button className="action-card" onClick={() => { setActiveTab('products'); setTimeout(() => handleAddProduct(), 100); }}>
                        <span className="action-icon"><FiPackage /></span>
                        <span><strong>محصول جدید</strong><small>افزودن فایل دانلودی</small></span>
                        <FiChevronLeft className="action-arrow" />
                      </button>
                      <button className="action-card" onClick={() => { setActiveTab('articles'); setTimeout(() => handleAddArticle(), 100); }}>
                        <span className="action-icon"><FiFileText /></span>
                        <span><strong>مقاله جدید</strong><small>انتشار محتوای تازه</small></span>
                        <FiChevronLeft className="action-arrow" />
                      </button>
                      <button className="action-card" onClick={() => { setActiveTab('users'); setTimeout(() => handleAddUser(), 100); }}>
                        <span className="action-icon"><FiUserPlus /></span>
                        <span><strong>کاربر جدید</strong><small>ایجاد حساب کاربری</small></span>
                        <FiChevronLeft className="action-arrow" />
                      </button>
                      <button className="action-card" onClick={() => setActiveTab('settings')}>
                        <span className="action-icon"><FiSettings /></span>
                        <span><strong>تنظیمات</strong><small>ظاهر و اطلاعات سایت</small></span>
                        <FiChevronLeft className="action-arrow" />
                      </button>
                    </div>
                  </section>
                </div>
              </div>
            )}

            {activeTab === 'analytics' && (
              <div key="analytics" className="admin-content">
                <AdminAnalytics />
              </div>
            )}

        {activeTab === 'users' && (
          <div key="users" className="admin-content">
            <div className="section-panel">
              <div className="panel-header-section">
                <div className="panel-heading">
                  <span className="panel-heading-icon"><FiUsers /></span>
                  <div>
                    <h2>فهرست کاربران</h2>
                    <p>{users.length.toLocaleString('fa-IR')} حساب کاربری در سیستم ثبت شده است.</p>
                  </div>
                </div>
                <button type="button" className="add-btn" onClick={handleAddUser}><FiPlus /> افزودن کاربر جدید</button>
              </div>

              {showUserForm && (
                <AdminFormModal
                  title={editingUser ? 'ویرایش اطلاعات کاربر' : 'افزودن کاربر جدید'}
                  eyebrow="مدیریت کاربران"
                  description="اطلاعات حساب، سطح دسترسی و تصویر پروفایل کاربر را تکمیل کنید."
                  icon={<FiUserPlus />}
                  onClose={closeUserForm}
                >
                  <form onSubmit={handleSubmitUser} className="admin-entity-form">
                    <div className="admin-form-grid">
                      <div className="admin-field admin-field-half">
                        <label htmlFor="user-name">نام و نام خانوادگی <span>*</span></label>
                        <input
                          id="user-name"
                          type="text"
                          name="name"
                          value={userFormData.name}
                          onChange={handleUserFormChange}
                          required
                          autoFocus
                          autoComplete="name"
                          placeholder="مثلاً مسعود اختیاری"
                        />
                        <small>نامی که در پنل و سفارش‌ها نمایش داده می‌شود.</small>
                      </div>
                      <div className="admin-field admin-field-half">
                        <label htmlFor="user-email">آدرس ایمیل <span>*</span></label>
                        <input
                          id="user-email"
                          type="email"
                          name="email"
                          value={userFormData.email}
                          onChange={handleUserFormChange}
                          required
                          autoComplete="email"
                          className="admin-ltr-input"
                          placeholder="example@email.com"
                        />
                        <small>ایمیل باید یکتا و در دسترس کاربر باشد.</small>
                      </div>
                      <div className="admin-field admin-field-half">
                        <label htmlFor="user-phone">شماره موبایل</label>
                        <input
                          id="user-phone"
                          type="tel"
                          name="phone"
                          value={userFormData.phone}
                          onChange={handleUserFormChange}
                          autoComplete="tel"
                          className="admin-ltr-input"
                          placeholder="09123456789"
                        />
                        <small>برای دریافت اعلان‌های پیامکی حساب استفاده می‌شود.</small>
                      </div>
                      <div className="admin-field admin-field-half">
                        <label htmlFor="user-password">رمز عبور {!editingUser && <span>*</span>}</label>
                        <input
                          id="user-password"
                          type="password"
                          name="password"
                          value={userFormData.password}
                          onChange={handleUserFormChange}
                          required={!editingUser}
                          autoComplete="new-password"
                          className="admin-ltr-input"
                          placeholder={editingUser ? 'برای حفظ رمز فعلی خالی بگذارید' : 'حداقل ۶ کاراکتر'}
                        />
                        <small>{editingUser ? 'تنها در صورت نیاز به تغییر رمز، این فیلد را پر کنید.' : 'یک رمز امن با حداقل ۶ کاراکتر انتخاب کنید.'}</small>
                      </div>
                      <div className="admin-field admin-field-half">
                        <label htmlFor="user-role">سطح دسترسی <span>*</span></label>
                        <select
                          id="user-role"
                          name="role"
                          value={userFormData.role}
                          onChange={handleUserFormChange}
                          required
                        >
                          <option value="user">کاربر عادی</option>
                          <option value="admin">مدیر</option>
                        </select>
                        <small>مدیر به بخش مدیریت و اطلاعات سایت دسترسی خواهد داشت.</small>
                      </div>
                      <div className="admin-field admin-field-full">
                        <label htmlFor="user-avatar">تصویر پروفایل</label>
                        <input
                          id="user-avatar"
                          type="url"
                          name="avatar"
                          value={userFormData.avatar}
                          onChange={handleUserFormChange}
                          className="admin-ltr-input"
                          placeholder="https://example.com/avatar.webp"
                        />
                        <small>نشانی مستقیم یک تصویر مربع را وارد کنید؛ این فیلد اختیاری است.</small>
                        {userFormData.avatar && (
                          <div className="admin-image-preview admin-avatar-preview">
                            <img
                              src={userFormData.avatar}
                              alt="پیش‌نمایش تصویر پروفایل"
                              onLoad={(e) => { e.currentTarget.style.display = 'block'; }}
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <span>پیش‌نمایش تصویر پروفایل</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="admin-form-actions">
                        <button type="submit" className="admin-primary-action">
                          <FiCheck aria-hidden="true" />
                          {editingUser ? 'ذخیره تغییرات کاربر' : 'ایجاد حساب کاربری'}
                        </button>
                        <button type="button" className="admin-secondary-action" onClick={closeUserForm}>
                          <FiArrowRight aria-hidden="true" />
                          بازگشت بدون ذخیره
                        </button>
                    </div>
                  </form>
                </AdminFormModal>
              )}

              <div className="search-section">
                <FiSearch aria-hidden="true" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="جستجو بر اساس نام یا ایمیل..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>شناسه</th>
                      <th>عکس</th>
                      <th>نام</th>
                      <th>ایمیل</th>
                      <th>نقش</th>
                      <th>تاریخ ثبت</th>
                      <th>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                          {searchTerm ? 'نتیجه‌ای یافت نشد' : 'هیچ کاربری ثبت نشده است'}
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => (
                        <tr key={user.id || user._id}>
                          <td>#{user.id || user._id}</td>
                          <td>
                            <div className="user-avatar-cell">
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.name} onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }} />
                              ) : null}
                              <div className="avatar-placeholder" style={{ display: user.avatar ? 'none' : 'flex' }}>
                                👤
                              </div>
                            </div>
                          </td>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>
                            <span className={`role-badge ${user.role === 'admin' ? 'admin-badge' : 'user-badge'}`}>
                              {user.role === 'admin' ? 'مدیر' : 'کاربر'}
                            </span>
                          </td>
                          <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString('fa-IR') : '—'}</td>
                          <td>
                            <div className="action-buttons">
                              <button type="button" className="edit-btn" onClick={() => handleEditUser(user)}><FiEdit2 /> ویرایش</button>
                              <button type="button" className="delete-btn" onClick={() => handleDeleteUser(user.id || user._id)}><FiTrash2 /> حذف</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'services' && (
          <div key="services" className="admin-content">
            <div className="section-panel">
              <div className="panel-header-section">
                <div className="panel-heading">
                  <span className="panel-heading-icon"><FiBriefcase /></span>
                  <div>
                    <h2>فهرست خدمات</h2>
                    <p>{services.length.toLocaleString('fa-IR')} خدمت برای نمایش در وب‌سایت تعریف شده است.</p>
                  </div>
                </div>
                <button type="button" className="add-btn" onClick={handleAddService}><FiPlus /> افزودن خدمات جدید</button>
              </div>

              {showServiceForm && (
                <AdminFormModal
                  title={editingService ? 'ویرایش خدمات' : 'افزودن خدمات جدید'}
                  eyebrow="مدیریت خدمات"
                  description="تمام محتوای صفحه خدمت، پلن‌ها، گالری، نمونه‌کارها و تنظیمات انتشار را مدیریت کنید."
                  icon={<FiBriefcase />}
                  onClose={closeServiceForm}
                >
                  <AdminServiceEditor
                    value={serviceFormData}
                    onChange={handleServiceFormDataChange}
                    onSubmit={handleSubmitService}
                    onCancel={closeServiceForm}
                    isEditing={Boolean(editingService)}
                    entityId={editingService?.id || editingService?._id}
                  />
                </AdminFormModal>
              )}

              <div className="search-section">
                <FiSearch aria-hidden="true" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="جستجو بر اساس عنوان یا توضیحات..."
                  value={serviceSearchTerm}
                  onChange={(e) => setServiceSearchTerm(e.target.value)}
                />
              </div>

              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>شناسه</th>
                      <th>تصویر</th>
                      <th>عنوان</th>
                      <th>توضیحات</th>
                      <th>محتوا</th>
                      <th>وضعیت</th>
                      <th>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredServices.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                          {serviceSearchTerm ? 'نتیجه‌ای یافت نشد' : 'هیچ خدماتی ثبت نشده است'}
                        </td>
                      </tr>
                    ) : (
                      filteredServices.map((service) => (
                        <tr key={service.id || service._id}>
                          <td>#{service.id || service._id}</td>
                          <td>
                            {service.image
                              ? <img src={resolveServiceMediaUrl(service.image)} alt="" className="admin-service-table-image" />
                              : <span className="admin-service-table-icon"><ServiceIcon name={service.icon} /></span>}
                          </td>
                          <td><strong>{service.title}</strong></td>
                          <td style={{ maxWidth: '300px' }}>{service.description}</td>
                          <td>
                            <span className="admin-service-content-counts">
                              {service.features?.length || 0} ویژگی · {service.planGroups?.reduce((count, group) => count + (group.plans?.length || 0), 0) || 0} پلن
                              {' · '}
                              {service.planGroups?.filter((group) => group.mode === 'request').length || 0} مسیر برآورد
                              {' · '}
                              {service.works?.length || 0} نمونه‌کار
                            </span>
                          </td>
                          <td><span className={`status-badge ${service.isActive !== false ? 'completed' : 'cancelled'}`}>{service.isActive !== false ? 'فعال' : 'غیرفعال'}</span></td>
                          <td>
                            <div className="action-buttons">
                              <button 
                                className="edit-btn"
                                onClick={() => handleEditService(service)}
                              >
                                <FiEdit2 /> ویرایش
                              </button>
                              <button type="button" className="delete-btn" onClick={() => handleDeleteService(service.id || service._id)}><FiTrash2 /> حذف</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div key="products" className="admin-content">
            <div className="section-panel">
              <div className="panel-header-section">
                <div className="panel-heading">
                  <span className="panel-heading-icon"><FiPackage /></span>
                  <div>
                    <h2>فهرست محصولات</h2>
                    <p>{products.length.toLocaleString('fa-IR')} محصول دانلودی در فروشگاه قرار دارد.</p>
                  </div>
                </div>
                <button type="button" className="add-btn" onClick={handleAddProduct}><FiPlus /> افزودن محصول جدید</button>
              </div>

              <HomeProductShowcaseSettings products={products} categories={productCategories} />

              {showProductForm && (
                <AdminFormModal
                  title={editingProduct ? 'ویرایش محصول' : 'افزودن محصول جدید'}
                  eyebrow="مدیریت محصولات"
                  description="مشخصات فروش، تصویر و مزیت‌های محصول را برای نمایش در فروشگاه تکمیل کنید."
                  icon={<FiPackage />}
                  onClose={closeProductForm}
                >
                  <form onSubmit={handleSubmitProduct} className="admin-entity-form admin-tabbed-entity-form">
                    <AdminEntityEditorTabs
                      tabs={PRODUCT_EDITOR_TABS}
                      activeTab={productEditorTab}
                      onChange={setProductEditorTab}
                      label="بخش‌های ویرایش محصول"
                      idPrefix="product-editor"
                    />
                    {!editingProduct && (
                      <div className="admin-translations-loading admin-editor-translation-note">
                        پس از ایجاد محصول، آیکون زبان کنار هر فیلد فعال می‌شود و ترجمه همان فیلد را خودکار ذخیره می‌کند.
                      </div>
                    )}
                    {productEditorTab === 'overview' && (
                      <section
                        className="admin-editor-panel admin-form-grid"
                        role="tabpanel"
                        id="product-editor-overview-panel"
                        aria-labelledby="product-editor-overview-tab"
                      >
                      <AdminEditorPanelHeading
                        title="اطلاعات پایه و قیمت‌گذاری"
                        description="نام، دسته‌بندی، آدرس و قیمت‌های محصول را در این بخش مدیریت کنید."
                      />
                      {editingProduct && (
                        <div className="admin-product-statistics" aria-label="آمار محصول">
                          <div className="admin-product-stat-card views">
                            <span><FiEye aria-hidden="true" /></span>
                            <div>
                              <small>تعداد بازدید</small>
                              <strong>{Number(editingProduct.viewsCount || 0).toLocaleString('fa-IR')}</strong>
                            </div>
                          </div>
                          <div className="admin-product-stat-card sales">
                            <span><FiShoppingBag aria-hidden="true" /></span>
                            <div>
                              <small>فروش موفق</small>
                              <strong>{Number(editingProduct.salesCount || 0).toLocaleString('fa-IR')}</strong>
                            </div>
                          </div>
                          <p>این آمار به‌صورت خودکار ثبت می‌شود و قابل ویرایش دستی نیست.</p>
                        </div>
                      )}
                      <div className="admin-field admin-field-two-third">
                        <label htmlFor="product-name">
                          نام محصول <span>*</span>
                          <AdminFieldTranslation entityType="product" entityId={editingProduct?.id || editingProduct?._id} fieldPath="name" fieldLabel="نام محصول" sourceValue={productFormData.name} />
                        </label>
                        <input
                          id="product-name"
                          type="text"
                          name="name"
                          value={productFormData.name}
                          onChange={handleProductFormChange}
                          required
                          autoFocus
                          placeholder="مثلاً افزونه رزرو آنلاین وردپرس"
                        />
                        <small>نام کامل و قابل جستجوی محصول را بنویسید.</small>
                      </div>
                      <div className="admin-field admin-field-third">
                        <div className="admin-field-label-row">
                          <label htmlFor="product-category">
                            دسته‌بندی <span>*</span>
                            <AdminFieldTranslation entityType="product" entityId={editingProduct?.id || editingProduct?._id} fieldPath="category" fieldLabel="دسته‌بندی" sourceValue={productFormData.category} />
                          </label>
                          <button
                            type="button"
                            className="admin-text-action"
                            onClick={() => {
                              setShowCategoryManager((current) => !current);
                              setCategoryError('');
                            }}
                          >
                            <FiTag aria-hidden="true" /> مدیریت
                          </button>
                        </div>
                        <select
                          id="product-category"
                          name="category"
                          value={productFormData.category}
                          onChange={handleProductFormChange}
                          required
                        >
                          {productCategories.length === 0 && <option value="">بدون دسته‌بندی</option>}
                          {productCategories.map((category) => (
                            <option key={category.id || category._id} value={category.name}>{category.name}</option>
                          ))}
                        </select>
                        <small>محصول در این گروه نمایش داده می‌شود.</small>
                        {showCategoryManager && (
                          <div className="admin-category-manager">
                            <div className="admin-category-create">
                              <input
                                type="text"
                                value={newCategoryName}
                                onChange={(event) => setNewCategoryName(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    handleCreateCategory();
                                  }
                                }}
                                placeholder="نام دسته‌بندی جدید"
                                maxLength="60"
                              />
                              <button type="button" onClick={handleCreateCategory} disabled={categoryBusy} aria-label="افزودن دسته‌بندی">
                                <FiPlus aria-hidden="true" />
                              </button>
                            </div>
                            {categoryError && <p className="admin-category-error">{categoryError}</p>}
                            <div className="admin-category-list">
                              {productCategories.map((category) => {
                                const categoryId = category.id || category._id;
                                const isEditing = editingCategoryId === categoryId;
                                return (
                                  <div className="admin-category-item" key={categoryId}>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editingCategoryName}
                                        onChange={(event) => setEditingCategoryName(event.target.value)}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter') {
                                            event.preventDefault();
                                            handleUpdateCategory(category);
                                          }
                                        }}
                                        autoFocus
                                        maxLength="60"
                                      />
                                    ) : (
                                      <span>{category.name}</span>
                                    )}
                                    <div>
                                      {isEditing ? (
                                        <>
                                          <button type="button" onClick={() => handleUpdateCategory(category)} disabled={categoryBusy} aria-label="ذخیره نام دسته‌بندی"><FiCheck /></button>
                                          <button type="button" onClick={() => { setEditingCategoryId(null); setEditingCategoryName(''); }} aria-label="لغو ویرایش"><FiX /></button>
                                        </>
                                      ) : (
                                        <>
                                          <button type="button" onClick={() => { setEditingCategoryId(categoryId); setEditingCategoryName(category.name); setCategoryError(''); }} aria-label="ویرایش دسته‌بندی"><FiEdit2 /></button>
                                          <button type="button" className="danger" onClick={() => handleDeleteCategory(category)} disabled={categoryBusy} aria-label="حذف دسته‌بندی"><FiTrash2 /></button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="admin-field admin-field-half">
                        <label htmlFor="product-price">
                          قیمت فروش <span>*</span>
                          <AdminMarketPrice entityType="product" entityId={editingProduct?.id || editingProduct?._id} field="price" fieldLabel="قیمت فروش" sourceValue={productFormData.price} />
                        </label>
                        <div className="admin-input-with-suffix">
                          <input
                            id="product-price"
                            type="text"
                            inputMode="numeric"
                            name="price"
                            value={productFormData.price}
                            onChange={handleProductFormChange}
                            required
                            placeholder="مثلاً 1,500,000"
                          />
                          <span>تومان</span>
                        </div>
                        <small>مبلغ را به تومان وارد کنید.</small>
                      </div>
                      <div className="admin-field admin-field-half">
                        <label htmlFor="product-discount-price">
                          قیمت تخفیفی
                          <AdminMarketPrice entityType="product" entityId={editingProduct?.id || editingProduct?._id} field="discountPrice" fieldLabel="قیمت تخفیفی" sourceValue={productFormData.discountPrice} />
                        </label>
                        <div className="admin-input-with-suffix">
                          <input
                          id="product-discount-price"
                          type="text"
                          inputMode="numeric"
                          name="discountPrice"
                          value={productFormData.discountPrice}
                          onChange={handleProductFormChange}
                          placeholder="مثلاً 1,200,000"
                          />
                          <span>تومان</span>
                        </div>
                        {Number(String(productFormData.discountPrice).replace(/,/g, '')) > 0 && Number(String(productFormData.price).replace(/,/g, '')) > Number(String(productFormData.discountPrice).replace(/,/g, '')) ? (
                          <small className="admin-discount-hint">
                            {Math.round((1 - Number(String(productFormData.discountPrice).replace(/,/g, '')) / Number(String(productFormData.price).replace(/,/g, ''))) * 100).toLocaleString('fa-IR')}٪ تخفیف برای مشتری
                          </small>
                        ) : (
                          <small>در صورت نداشتن تخفیف، این فیلد را خالی بگذارید.</small>
                        )}
                      </div>
                      <div className="admin-field admin-field-full">
                        <label htmlFor="product-slug">
                          نامک آدرس محصول
                          <AdminFieldTranslation entityType="product" entityId={editingProduct?.id || editingProduct?._id} fieldPath="slug" fieldLabel="نامک آدرس محصول" sourceValue={productFormData.slug} />
                        </label>
                        <input
                          id="product-slug"
                          type="text"
                          name="slug"
                          dir="ltr"
                          value={productFormData.slug}
                          onChange={handleProductFormChange}
                          maxLength="120"
                          placeholder="wordpress-booking-plugin"
                        />
                        <small>
                          برای آدرس سئو استفاده می‌شود؛ مثلاً haryad.com/products/wordpress-booking-plugin.
                          اگر خالی باشد، از نام محصول ساخته می‌شود و پس از انتشار بهتر است تغییر نکند.
                        </small>
                      </div>
                      </section>
                    )}
                    {productEditorTab === 'media' && (
                      <section
                        className="admin-editor-panel admin-form-grid"
                        role="tabpanel"
                        id="product-editor-media-panel"
                        aria-labelledby="product-editor-media-tab"
                      >
                      <AdminEditorPanelHeading
                        title="تصاویر و فایل قابل دانلود"
                        description="تصاویر فارسی و ترجمه‌شده و فایل خصوصی محصول را از یک بخش مدیریت کنید."
                      />
                      <div className="admin-field admin-field-full">
                        <div className="admin-field-label-row admin-product-images-heading">
                          <div>
                            <div className="admin-product-images-title-row">
                              <label>تصاویر محصول</label>
                              <AdminProductImagesTranslation
                                productId={editingProduct?.id || editingProduct?._id}
                                sourceImages={{
                                  cardImageUrl: productFormData.cardImageUrl,
                                  detailImageUrl: productFormData.detailImageUrl,
                                }}
                              />
                            </div>
                            <small>برای هر محل نمایش، تصویر مناسب همان قاب و زبان را انتخاب کنید.</small>
                          </div>
                          <span className="admin-image-count-badge">۲ تصویر مستقل</span>
                        </div>
                        <div className="admin-product-images-grid">
                          {PRODUCT_IMAGE_FIELDS.map((imageField) => {
                            const imageValue = productFormData[imageField.name];
                            const isUploading = productImageUploading === imageField.name;
                            return (
                              <section className="admin-product-image-group" key={imageField.name}>
                                <div className="admin-product-image-group-heading">
                                  <div>
                                    <strong>{imageField.title}</strong>
                                    <span>{imageField.help}</span>
                                  </div>
                                  <b>{imageField.badge}</b>
                                </div>
                                <div className="admin-product-image-editor">
                                  <div className="admin-upload-column">
                                    <input
                                      id={imageField.uploadId}
                                      type="file"
                                      accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                                      className="admin-upload-input"
                                      onChange={(event) => handleProductImageUpload(event, imageField.name)}
                                      disabled={Boolean(productImageUploading)}
                                    />
                                    <label htmlFor={imageField.uploadId} className={`admin-upload-zone ${isUploading ? 'uploading' : ''}`}>
                                      <FiUploadCloud aria-hidden="true" />
                                      <strong>{isUploading ? 'در حال آپلود تصویر...' : 'آپلود از کامپیوتر'}</strong>
                                      <span>JPG، PNG، WEBP، GIF یا AVIF تا ۵ مگابایت</span>
                                    </label>
                                    {productImageError[imageField.name] && (
                                      <p className="admin-upload-error">{productImageError[imageField.name]}</p>
                                    )}
                                  </div>
                                  <div className="admin-image-url-column">
                                    <label htmlFor={imageField.urlId}>یا آدرس مستقیم تصویر</label>
                                    <input
                                      id={imageField.urlId}
                                      type="url"
                                      name={imageField.name}
                                      value={getDirectMediaUrlInputValue(imageValue)}
                                      onChange={handleProductFormChange}
                                      className="admin-ltr-input"
                                      placeholder={imageValue.startsWith('/')
                                        ? 'برای جایگزینی تصویر، یک آدرس کامل وارد کنید'
                                        : imageField.placeholder}
                                    />
                                  </div>
                                </div>
                                {imageValue ? (
                                  <div className={`admin-product-image-preview ${imageField.previewClass}`}>
                                    <img src={resolveMediaUrl(imageValue)} alt={`پیش‌نمایش ${imageField.title}`} />
                                    <div>
                                      <strong>پیش‌نمایش {imageField.title}</strong>
                                      <span>{imageValue.startsWith('/uploads/') ? 'آپلود شده از کامپیوتر' : 'بارگذاری شده از نشانی اینترنتی'}</span>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setProductFormData((current) => ({ ...current, [imageField.name]: '' }))}
                                    >
                                      <FiTrash2 aria-hidden="true" /> حذف تصویر
                                    </button>
                                  </div>
                                ) : (
                                  <div className="admin-product-image-empty">هنوز تصویری برای این بخش انتخاب نشده است.</div>
                                )}
                              </section>
                            );
                          })}
                        </div>
                      </div>
                      <div className="admin-field admin-field-full">
                        <div className="admin-field-label-row">
                          <label>فایل قابل دانلود محصول</label>
                          <span className="admin-private-file-badge">دانلود امن پس از پرداخت</span>
                        </div>
                        <div className="admin-product-file-editor">
                          <input
                            id="product-file-upload"
                            type="file"
                            accept=".zip,application/zip,application/x-zip-compressed"
                            className="admin-upload-input"
                            onChange={handleProductFileUpload}
                            disabled={!editingProduct || productFileUploading}
                          />
                          <label
                            htmlFor={editingProduct ? 'product-file-upload' : undefined}
                            className={`admin-upload-zone admin-product-file-zone ${productFileUploading ? 'uploading' : ''} ${!editingProduct ? 'disabled' : ''}`}
                          >
                            <FiDownload aria-hidden="true" />
                            <strong>
                              {productFileUploading
                                ? 'در حال آپلود فایل محصول...'
                                : editingProduct?.downloadFile?.originalName
                                  ? 'جایگزینی فایل ZIP محصول'
                                  : 'انتخاب فایل ZIP محصول'}
                            </strong>
                            <span>حداکثر حجم مجاز {Number(siteSettings.store.maxProductFileSizeMb || 200).toLocaleString('fa-IR')} مگابایت</span>
                          </label>

                          {editingProduct?.downloadFile?.originalName ? (
                            <div className="admin-product-file-current">
                              <span className="admin-product-file-icon"><FiPackage aria-hidden="true" /></span>
                              <div>
                                <strong>{editingProduct.downloadFile.originalName}</strong>
                                <span>
                                  {formatFileSize(editingProduct.downloadFile.size)}
                                  {editingProduct.downloadFile.uploadedAt
                                    ? ` · آپلود در ${new Date(editingProduct.downloadFile.uploadedAt).toLocaleDateString('fa-IR')}`
                                    : ''}
                                </span>
                              </div>
                              <button type="button" onClick={handleDeleteProductFile} disabled={productFileUploading}>
                                <FiTrash2 aria-hidden="true" /> حذف فایل
                              </button>
                            </div>
                          ) : (
                            <div className="admin-product-file-note">
                              {editingProduct
                                ? 'هنوز فایل دانلودی برای این محصول قرار نگرفته است.'
                                : 'ابتدا محصول را ایجاد کنید؛ سپس از صفحه ویرایش، فایل ZIP را آپلود کنید.'}
                            </div>
                          )}
                        </div>
                        {productFileError && <p className="admin-upload-error">{productFileError}</p>}
                        <small>فایل در فضای خصوصی سرور نگهداری می‌شود و فقط خریدار دارای پرداخت موفق می‌تواند آن را دریافت کند.</small>
                      </div>
                      </section>
                    )}
                    {productEditorTab === 'content' && (
                      <section
                        className="admin-editor-panel admin-form-grid"
                        role="tabpanel"
                        id="product-editor-content-panel"
                        aria-labelledby="product-editor-content-tab"
                      >
                      <AdminEditorPanelHeading
                        title="توضیحات، تصاویر داخل متن و ویژگی‌ها"
                        description="محتوای کامل فارسی و نسخه‌های مستقل کوردی و انگلیسی را در این بخش بنویسید."
                      />
                      <div className="admin-field admin-field-full">
                        <div className="admin-field-label-row admin-product-description-label-row">
                          <label htmlFor="product-description">توضیحات محصول <span>*</span></label>
                          <button
                            type="button"
                            className={`admin-product-description-translation-trigger ${showProductDescriptionTranslations ? 'is-active' : ''}`}
                            onClick={() => setShowProductDescriptionTranslations((current) => !current)}
                            disabled={!editingProduct}
                            aria-expanded={showProductDescriptionTranslations}
                            aria-controls="admin-product-description-translations"
                            title={editingProduct
                              ? showProductDescriptionTranslations
                                ? 'بستن ویرایشگر متن و تصاویر زبان‌های دیگر'
                                : 'ویرایش متن و تصاویر توضیحات در زبان‌های دیگر'
                              : 'ابتدا محصول را ذخیره کنید'}
                          >
                            <FiGlobe aria-hidden="true" />
                            <FiImage aria-hidden="true" />
                            <span>{showProductDescriptionTranslations ? 'بستن متن و تصاویر زبان‌های دیگر' : 'متن و تصاویر زبان‌های دیگر'}</span>
                          </button>
                        </div>
                        {showProductDescriptionTranslations && editingProduct && (
                          <div id="admin-product-description-translations">
                            <AdminProductDescriptionTranslation
                              productId={editingProduct.id || editingProduct._id}
                              sourceDescription={productFormData.description}
                              onClose={() => setShowProductDescriptionTranslations(false)}
                            />
                          </div>
                        )}
                        <ProductRichTextEditor
                          value={productFormData.description}
                          onChange={(description) => setProductFormData((current) => ({ ...current, description }))}
                          onUploadImage={handleProductDescriptionImageUpload}
                        />
                        <small>متن قالب‌بندی‌شده، لینک‌ها و تصاویر در صفحه معرفی محصول نمایش داده می‌شوند.</small>
                      </div>
                      <div className="admin-field admin-field-full">
                        <label htmlFor="product-features">
                          ویژگی‌های محصول
                          <AdminFieldTranslation entityType="product" entityId={editingProduct?.id || editingProduct?._id} fieldPath="features" fieldLabel="ویژگی‌های محصول" sourceValue={productFormData.features} multiline />
                        </label>
                        <textarea
                          id="product-features"
                          name="features"
                          value={productFormData.features}
                          onChange={handleProductFormChange}
                          placeholder="نصب آسان&#10;به‌روزرسانی رایگان&#10;شش ماه پشتیبانی"
                          rows="6"
                        />
                        <small>هر ویژگی را در یک خط جداگانه بنویسید.</small>
                      </div>
                      </section>
                    )}
                    {productEditorTab === 'release' && (
                      <section
                        className="admin-editor-panel admin-form-grid"
                        role="tabpanel"
                        id="product-editor-release-panel"
                        aria-labelledby="product-editor-release-tab"
                      >
                      <AdminEditorPanelHeading
                        title="SEO و اطلاعات انتشار"
                        description="نمایش محصول در موتورهای جستجو و اطلاعات نسخه منتشرشده را تنظیم کنید."
                      />

                      <div className="admin-form-section-title">
                        <span><FiSearch aria-hidden="true" /></span>
                        <div>
                          <h4>تنظیمات سئو</h4>
                          <p>عنوان و توضیحی که در موتورهای جستجو و هنگام اشتراک‌گذاری نمایش داده می‌شود.</p>
                        </div>
                      </div>
                      <div className="admin-field admin-field-full">
                        <label htmlFor="product-seo-title">
                          عنوان SEO
                          <AdminFieldTranslation entityType="product" entityId={editingProduct?.id || editingProduct?._id} fieldPath="seoTitle" fieldLabel="عنوان SEO" sourceValue={productFormData.seoTitle} />
                        </label>
                        <input id="product-seo-title" type="text" name="seoTitle" value={productFormData.seoTitle} onChange={handleProductFormChange} maxLength="120" placeholder="عنوان پیشنهادی برای نتیجه جستجو" />
                        <small>{productFormData.seoTitle.length.toLocaleString('fa-IR')} کاراکتر؛ حدود ۵۰ تا ۶۰ کاراکتر مناسب است.</small>
                      </div>
                      <div className="admin-field admin-field-full">
                        <label htmlFor="product-seo-description">
                          توضیحات SEO
                          <AdminFieldTranslation entityType="product" entityId={editingProduct?.id || editingProduct?._id} fieldPath="seoDescription" fieldLabel="توضیحات SEO" sourceValue={productFormData.seoDescription} multiline />
                        </label>
                        <textarea id="product-seo-description" name="seoDescription" value={productFormData.seoDescription} onChange={handleProductFormChange} maxLength="320" rows="4" placeholder="خلاصه‌ای دقیق و ترغیب‌کننده برای موتورهای جستجو" />
                        <small>{productFormData.seoDescription.length.toLocaleString('fa-IR')} کاراکتر؛ حدود ۱۴۰ تا ۱۶۰ کاراکتر مناسب است.</small>
                      </div>

                      <div className="admin-form-section-title">
                        <span><FiPackage aria-hidden="true" /></span>
                        <div>
                          <h4>اطلاعات انتشار و نسخه</h4>
                          <p>این اطلاعات در ستون مشخصات صفحه محصول نمایش داده می‌شوند.</p>
                        </div>
                      </div>
                      <div className="admin-field admin-field-third">
                        <label htmlFor="product-version">نسخه محصول</label>
                        <input id="product-version" type="text" name="version" value={productFormData.version} onChange={handleProductFormChange} placeholder="مثلاً 2.1.0" className="admin-ltr-input" />
                        <small>شماره آخرین نسخه قابل دریافت.</small>
                      </div>
                      <div className="admin-field admin-field-third">
                        <label htmlFor="product-release-date">تاریخ انتشار</label>
                        <input id="product-release-date" type="date" name="releaseDate" value={productFormData.releaseDate} onChange={handleProductFormChange} className="admin-ltr-input" />
                        <small>تاریخ اولین انتشار محصول.</small>
                      </div>
                      <div className="admin-field admin-field-third">
                        <label htmlFor="product-last-updated">تاریخ بروزرسانی</label>
                        <input id="product-last-updated" type="date" name="lastUpdated" value={productFormData.lastUpdated} onChange={handleProductFormChange} className="admin-ltr-input" />
                        <small>تاریخ انتشار آخرین بروزرسانی.</small>
                      </div>
                      </section>
                    )}
                    {productEditorTab === 'changelog' && (
                      <section
                        className="admin-editor-panel admin-form-grid"
                        role="tabpanel"
                        id="product-editor-changelog-panel"
                        aria-labelledby="product-editor-changelog-tab"
                      >
                      <AdminEditorPanelHeading
                        title="تاریخچه نسخه‌ها و تغییرات"
                        description="هر نسخه را همراه تاریخ و فهرست تغییرات آن جداگانه ثبت کنید."
                      />

                      <div className="admin-field admin-field-full admin-repeatable-field">
                        <div className="admin-repeatable-heading">
                          <div>
                            <h4>لیست تغییرات</h4>
                            <p>برای هر نسخه، تغییرات آن را جداگانه ثبت کنید.</p>
                          </div>
                          <button type="button" onClick={addProductChangelog}><FiPlus /> افزودن نسخه</button>
                        </div>
                        {productFormData.changelog.length === 0 ? (
                          <div className="admin-repeatable-empty">هنوز تغییری برای این محصول ثبت نشده است.</div>
                        ) : (
                          <div className="admin-repeatable-list">
                            {productFormData.changelog
                              .map((entry, index) => ({ entry, index }))
                              .reverse()
                              .map(({ entry, index }) => (
                              <div className="admin-repeatable-item" key={`changelog-${index}`}>
                                <div className="admin-repeatable-item-head">
                                  <strong>نسخه {index + 1}</strong>
                                  <button type="button" onClick={() => removeProductChangelog(index)} aria-label="حذف نسخه"><FiTrash2 /></button>
                                </div>
                                <div className="admin-mini-grid">
                                  <div>
                                    <label htmlFor={`changelog-version-${index}`}>
                                      شماره نسخه
                                      <AdminFieldTranslation entityType="product" entityId={editingProduct?.id || editingProduct?._id} fieldPath={`changelog.${index}.version`} fieldLabel={`شماره نسخه ${index + 1}`} sourceValue={entry.version} />
                                    </label>
                                    <input id={`changelog-version-${index}`} type="text" value={entry.version} onChange={(event) => updateProductChangelog(index, 'version', event.target.value)} placeholder="2.1.0" className="admin-ltr-input" />
                                  </div>
                                  <div>
                                    <label htmlFor={`changelog-date-${index}`}>تاریخ</label>
                                    <input id={`changelog-date-${index}`} type="date" value={entry.date} onChange={(event) => updateProductChangelog(index, 'date', event.target.value)} className="admin-ltr-input" />
                                  </div>
                                  <div className="full">
                                    <label htmlFor={`changelog-items-${index}`}>
                                      تغییرات این نسخه
                                      <AdminFieldTranslation entityType="product" entityId={editingProduct?.id || editingProduct?._id} fieldPath={`changelog.${index}.items`} fieldLabel={`تغییرات نسخه ${index + 1}`} sourceValue={entry.items} multiline />
                                    </label>
                                    <textarea id={`changelog-items-${index}`} value={entry.items} onChange={(event) => updateProductChangelog(index, 'items', event.target.value)} placeholder="بهبود سرعت بارگذاری&#10;رفع مشکل فرم پرداخت&#10;افزودن قابلیت جدید" rows="4" />
                                    <small>هر تغییر را در یک خط جداگانه وارد کنید.</small>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      </section>
                    )}
                    {productEditorTab === 'faq' && (
                      <section
                        className="admin-editor-panel admin-form-grid"
                        role="tabpanel"
                        id="product-editor-faq-panel"
                        aria-labelledby="product-editor-faq-tab"
                      >
                      <AdminEditorPanelHeading
                        title="سوالات متداول محصول"
                        description="پرسش‌های پرتکرار مشتریان و پاسخ روشن هرکدام را مدیریت کنید."
                      />

                      <div className="admin-field admin-field-full admin-repeatable-field">
                        <div className="admin-repeatable-heading">
                          <div>
                            <h4>سوالات متداول</h4>
                            <p>پرسش‌های پرتکرار مشتریان و پاسخ روشن آن‌ها را ثبت کنید.</p>
                          </div>
                          <button type="button" onClick={addProductFaq}><FiPlus /> افزودن سوال</button>
                        </div>
                        {productFormData.faq.length === 0 ? (
                          <div className="admin-repeatable-empty">هنوز سوال متداولی برای این محصول ثبت نشده است.</div>
                        ) : (
                          <div className="admin-repeatable-list">
                            {productFormData.faq.map((item, index) => (
                              <div className="admin-repeatable-item" key={`faq-${index}`}>
                                <div className="admin-repeatable-item-head">
                                  <strong>سوال {index + 1}</strong>
                                  <button type="button" onClick={() => removeProductFaq(index)} aria-label="حذف سوال"><FiTrash2 /></button>
                                </div>
                                <div className="admin-mini-grid admin-faq-grid">
                                  <div>
                                    <label htmlFor={`faq-question-${index}`}>
                                      سوال
                                      <AdminFieldTranslation entityType="product" entityId={editingProduct?.id || editingProduct?._id} fieldPath={`faq.${index}.q`} fieldLabel={`سوال متداول ${index + 1}`} sourceValue={item.q} />
                                    </label>
                                    <input id={`faq-question-${index}`} type="text" value={item.q} onChange={(event) => updateProductFaq(index, 'q', event.target.value)} placeholder="مثلاً آیا بروزرسانی‌ها رایگان هستند؟" />
                                  </div>
                                  <div>
                                    <label htmlFor={`faq-answer-${index}`}>
                                      پاسخ
                                      <AdminFieldTranslation entityType="product" entityId={editingProduct?.id || editingProduct?._id} fieldPath={`faq.${index}.a`} fieldLabel={`پاسخ سوال ${index + 1}`} sourceValue={item.a} multiline />
                                    </label>
                                    <textarea id={`faq-answer-${index}`} value={item.a} onChange={(event) => updateProductFaq(index, 'a', event.target.value)} placeholder="پاسخ کامل و شفاف را بنویسید..." rows="3" />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      </section>
                    )}
                    <div className="admin-form-actions product-editor-actions">
                      {editingProduct ? (
                        <AdminProductTranslationPublish productId={editingProduct.id || editingProduct._id} />
                      ) : null}
                      <div className="product-editor-form-buttons">
                        <button type="submit" className="admin-primary-action" disabled={productImageUploading || productFileUploading || categoryBusy}>
                          <FiCheck aria-hidden="true" />
                          {editingProduct ? 'ذخیره تغییرات محصول' : 'ایجاد محصول جدید'}
                        </button>
                        <button type="button" className="admin-secondary-action" onClick={closeProductForm}>
                          <FiArrowRight aria-hidden="true" />
                          بازگشت بدون ذخیره
                        </button>
                      </div>
                    </div>
                  </form>
                </AdminFormModal>
              )}

              <div className="search-section">
                <FiSearch aria-hidden="true" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="جستجو بر اساس نام یا توضیحات..."
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                />
              </div>

              <div className="table-container admin-products-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>شناسه</th>
                      <th>تصویر</th>
                      <th>نام محصول</th>
                      <th>دسته‌بندی</th>
                      <th>توضیحات</th>
                      <th>قیمت</th>
                      <th>آمار</th>
                      <th>فایل محصول</th>
                      <th>تعداد ویژگی‌ها</th>
                      <th>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.length === 0 ? (
                      <tr>
                        <td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>
                          {productSearchTerm ? 'نتیجه‌ای یافت نشد' : 'هیچ محصولی ثبت نشده است'}
                        </td>
                      </tr>
                    ) : (
                      filteredProducts.map((product) => (
                        <tr key={product.id || product._id}>
                          <td>#{product.id || product._id}</td>
                          <td>
                            <div className="product-image-cell">
                              {(product.cardImageUrl || product.imageUrl) ? (
                                <img src={resolveMediaUrl(product.cardImageUrl || product.imageUrl)} alt={product.name} onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }} />
                              ) : null}
                              <span style={{ fontSize: '2rem', display: (product.cardImageUrl || product.imageUrl) ? 'none' : 'block' }}>
                                {product.image || '📦'}
                              </span>
                            </div>
                          </td>
                          <td><strong>{product.name}</strong></td>
                          <td>
                            {product.category ? (
                              <span className="category-badge">{product.category}</span>
                            ) : (
                              <span style={{ color: '#94a3b8' }}>بدون دسته‌بندی</span>
                            )}
                          </td>
                          <td className="admin-product-description-cell">
                            <span>{richTextToPlainText(product.description)}</span>
                          </td>
                          <td>
                            {Number(product.discountPrice) > 0 && Number(product.discountPrice) < Number(product.price) ? (
                              <div className="admin-table-price">
                                <del>{Number(product.price).toLocaleString('fa-IR')}</del>
                                <strong>{Number(product.discountPrice).toLocaleString('fa-IR')} تومان</strong>
                              </div>
                            ) : (
                              <strong>{typeof product.price === 'number' ? product.price.toLocaleString('fa-IR') : product.price} تومان</strong>
                            )}
                          </td>
                          <td>
                            <div className="admin-table-product-stats">
                              <span><FiEye /> {Number(product.viewsCount || 0).toLocaleString('fa-IR')}</span>
                              <span><FiShoppingBag /> {Number(product.salesCount || 0).toLocaleString('fa-IR')}</span>
                            </div>
                          </td>
                          <td>
                            {product.downloadFile?.originalName ? (
                              <span className="admin-file-ready"><FiDownload /> آماده</span>
                            ) : (
                              <span className="admin-file-missing">فاقد فایل</span>
                            )}
                          </td>
                          <td>{Array.isArray(product.features) ? product.features.length : 0}</td>
                          <td>
                            <div className="action-buttons">
                              <button type="button" className="edit-btn" onClick={() => handleEditProduct(product)}><FiEdit2 /> ویرایش</button>
                              <button type="button" className="delete-btn" onClick={() => handleDeleteProduct(product.id || product._id)}><FiTrash2 /> حذف</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ai-content-agent' && (
          <div key="ai-content-agent" className="admin-content">
            <AdminAiContentAgent
              articles={articles}
              articleCategories={articleCategories}
              onUsePackage={handleUseAiPackage}
              onRefreshArticles={fetchArticles}
              onOpenArticles={() => setActiveTab('articles')}
            />
          </div>
        )}

        {activeTab === 'articles' && (
          <div key="articles" className="admin-content">
            <div className="section-panel">
              <div className="panel-header-section">
                <div className="panel-heading">
                  <span className="panel-heading-icon"><FiFileText /></span>
                  <div>
                    <h2>فهرست مقالات</h2>
                    <p>{articles.length.toLocaleString('fa-IR')} مقاله در آرشیو محتوای سایت ثبت شده است.</p>
                  </div>
                </div>
                <button type="button" className="add-btn" onClick={handleAddArticle}><FiPlus /> افزودن مقاله جدید</button>
              </div>

              {showArticleForm && (
                <AdminFormModal
                  title={editingArticle ? 'ویرایش مقاله' : 'افزودن مقاله جدید'}
                  eyebrow="مدیریت مقالات"
                  description="عنوان، خلاصه و محتوای مقاله را برای انتشار در وب‌سایت آماده کنید."
                  icon={<FiFileText />}
                  onClose={closeArticleForm}
                >
                  <form onSubmit={handleSubmitArticle} className="admin-entity-form admin-tabbed-entity-form">
                    <AdminEntityEditorTabs
                      tabs={ARTICLE_EDITOR_TABS}
                      activeTab={articleEditorTab}
                      onChange={setArticleEditorTab}
                      label="بخش‌های ویرایش مقاله"
                      idPrefix="article-editor"
                    />
                    {!editingArticle && (
                      <div className="admin-translations-loading admin-editor-translation-note">
                        پس از ایجاد مقاله، آیکون زبان کنار هر فیلد فعال می‌شود و ترجمه همان فیلد را خودکار ذخیره می‌کند.
                      </div>
                    )}
                    {articleEditorTab === 'overview' && (
                      <section
                        className="admin-editor-panel admin-form-grid"
                        role="tabpanel"
                        id="article-editor-overview-panel"
                        aria-labelledby="article-editor-overview-tab"
                      >
                      <AdminEditorPanelHeading
                        title="اطلاعات پایه مقاله"
                        description="عنوان، دسته‌بندی و خلاصه‌ای را تنظیم کنید که در کارت مقاله نمایش داده می‌شود."
                      />
                      <div className="admin-field admin-field-two-third">
                        <label htmlFor="article-title">
                          عنوان مقاله <span>*</span>
                          <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="title" fieldLabel="عنوان مقاله" sourceValue={articleFormData.title} />
                        </label>
                        <input
                          id="article-title"
                          type="text"
                          name="title"
                          value={articleFormData.title}
                          onChange={handleArticleFormChange}
                          required
                          autoFocus
                          placeholder="یک عنوان روشن و جذاب بنویسید"
                        />
                        <small>عنوان بهتر است کوتاه، دقیق و مرتبط با موضوع باشد.</small>
                      </div>
                      <div className="admin-field admin-field-third">
                        <div className="admin-field-label-row">
                          <label htmlFor="article-category">
                            دسته‌بندی <span>*</span>
                            <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="category" fieldLabel="دسته‌بندی مقاله" sourceValue={articleFormData.category} />
                          </label>
                          <button
                            type="button"
                            className="admin-text-action"
                            onClick={() => {
                              setShowArticleCategoryManager((current) => !current);
                              setArticleCategoryError('');
                            }}
                          >
                            <FiTag aria-hidden="true" /> مدیریت
                          </button>
                        </div>
                        <select
                          id="article-category"
                          name="category"
                          value={articleFormData.category}
                          onChange={handleArticleFormChange}
                          required
                        >
                          {articleCategories.length === 0 && <option value="">بدون دسته‌بندی</option>}
                          {articleCategories.map((category) => (
                            <option key={category.id || category._id} value={category.name}>{category.name}</option>
                          ))}
                        </select>
                        <small>مقاله در این بخش دسته‌بندی می‌شود.</small>
                        {showArticleCategoryManager && (
                          <div className="admin-category-manager">
                            <div className="admin-category-create">
                              <input
                                type="text"
                                value={newArticleCategoryName}
                                onChange={(event) => setNewArticleCategoryName(event.target.value)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter') {
                                    event.preventDefault();
                                    handleCreateArticleCategory();
                                  }
                                }}
                                placeholder="نام دسته‌بندی جدید"
                                maxLength="60"
                              />
                              <button type="button" onClick={handleCreateArticleCategory} disabled={articleCategoryBusy} aria-label="افزودن دسته‌بندی مقاله">
                                <FiPlus aria-hidden="true" />
                              </button>
                            </div>
                            {articleCategoryError && <p className="admin-category-error">{articleCategoryError}</p>}
                            <div className="admin-category-list">
                              {articleCategories.map((category) => {
                                const categoryId = category.id || category._id;
                                const isEditing = editingArticleCategoryId === categoryId;
                                return (
                                  <div className="admin-category-item" key={categoryId}>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        value={editingArticleCategoryName}
                                        onChange={(event) => setEditingArticleCategoryName(event.target.value)}
                                        onKeyDown={(event) => {
                                          if (event.key === 'Enter') {
                                            event.preventDefault();
                                            handleUpdateArticleCategory(category);
                                          }
                                        }}
                                        autoFocus
                                        maxLength="60"
                                      />
                                    ) : (
                                      <span>{category.name}</span>
                                    )}
                                    <div>
                                      {isEditing ? (
                                        <>
                                          <button type="button" onClick={() => handleUpdateArticleCategory(category)} disabled={articleCategoryBusy} aria-label="ذخیره نام دسته‌بندی"><FiCheck /></button>
                                          <button type="button" onClick={() => { setEditingArticleCategoryId(null); setEditingArticleCategoryName(''); }} aria-label="لغو ویرایش"><FiX /></button>
                                        </>
                                      ) : (
                                        <>
                                          <button type="button" onClick={() => { setEditingArticleCategoryId(categoryId); setEditingArticleCategoryName(category.name); setArticleCategoryError(''); }} aria-label="ویرایش دسته‌بندی"><FiEdit2 /></button>
                                          <button type="button" className="danger" onClick={() => handleDeleteArticleCategory(category)} disabled={articleCategoryBusy} aria-label="حذف دسته‌بندی"><FiTrash2 /></button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="admin-field admin-field-full">
                        <label htmlFor="article-excerpt">
                          خلاصه مقاله <span>*</span>
                          <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="excerpt" fieldLabel="خلاصه مقاله" sourceValue={articleFormData.excerpt} multiline />
                        </label>
                        <textarea
                          id="article-excerpt"
                          name="excerpt"
                          value={articleFormData.excerpt}
                          onChange={handleArticleFormChange}
                          required
                          placeholder="در چند جمله کوتاه بگویید مخاطب در این مقاله چه چیزی یاد می‌گیرد..."
                          rows="4"
                        />
                        <small>این خلاصه در کارت مقاله و نتایج جستجو نمایش داده می‌شود.</small>
                      </div>
                      <div className="admin-field admin-field-full">
                        <label htmlFor="article-summary">
                          خلاصه محتوایی Agent
                          <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="summary" fieldLabel="خلاصه محتوایی" sourceValue={articleFormData.summary} multiline />
                        </label>
                        <textarea
                          id="article-summary"
                          name="summary"
                          value={articleFormData.summary}
                          onChange={handleArticleFormChange}
                          rows="4"
                          placeholder="خلاصه مستقل و فشرده مقاله؛ Agent این بخش را برای مرور سریع محتوا تولید می‌کند."
                        />
                        <small>این فیلد فقط برای مرور و مدیریت داخلی محتواست و در صفحه عمومی مقاله نمایش داده نمی‌شود.</small>
                      </div>
                      </section>
                    )}
                    {articleEditorTab === 'content' && (
                      <section
                        className="admin-editor-panel admin-form-grid"
                        role="tabpanel"
                        id="article-editor-content-panel"
                        aria-labelledby="article-editor-content-tab"
                      >
                      <AdminEditorPanelHeading
                        title="متن کامل و ترجمه مقاله"
                        description="متن، تصاویر و ویدیوهای مقاله را با ویرایشگر کامل آماده کنید."
                      />
                      <div className="admin-field admin-field-full">
                        <div className="admin-field-label-row admin-product-description-label-row">
                          <label htmlFor="article-content">متن کامل مقاله <span>*</span></label>
                          <button
                            type="button"
                            className={`admin-product-description-translation-trigger ${showArticleContentTranslations ? 'is-active' : ''}`}
                            onClick={() => setShowArticleContentTranslations((current) => !current)}
                            disabled={!editingArticle}
                            aria-expanded={showArticleContentTranslations}
                            aria-controls="admin-article-content-translations"
                            title={editingArticle
                              ? showArticleContentTranslations
                                ? 'بستن ویرایشگر متن و تصاویر زبان‌های دیگر'
                                : 'ویرایش متن و تصاویر مقاله در زبان‌های دیگر'
                              : 'ابتدا مقاله را ذخیره کنید'}
                          >
                            <FiGlobe aria-hidden="true" />
                            <FiImage aria-hidden="true" />
                            <span>{showArticleContentTranslations ? 'بستن متن و تصاویر زبان‌های دیگر' : 'متن و تصاویر زبان‌های دیگر'}</span>
                          </button>
                        </div>
                        {showArticleContentTranslations && editingArticle && (
                          <div id="admin-article-content-translations">
                            <AdminArticleContentTranslation
                              articleId={editingArticle.id || editingArticle._id}
                              sourceContent={articleFormData.content}
                              onClose={() => setShowArticleContentTranslations(false)}
                            />
                          </div>
                        )}
                        <ProductRichTextEditor
                          value={articleFormData.content}
                          onChange={(content) => setArticleFormData((current) => ({ ...current, content }))}
                          onUploadImage={handleArticleContentImageUpload}
                          enableVideo
                          onUploadVideo={handleArticleContentVideoUpload}
                          placeholder="متن کامل مقاله را با تیترها و پاراگراف‌بندی مناسب بنویسید..."
                          ariaLabel="متن کامل مقاله"
                          editorId="article-content"
                          imageAlt="تصویر مقاله"
                        />
                        <small>تیتر، لینک، فهرست، نقل‌قول، تصویر و ویدیوی YouTube، آپارات، Vimeo یا فایل MP4/WebM با قالب‌بندی کامل نمایش داده می‌شوند.</small>
                      </div>
                      <div className="admin-field admin-field-full admin-ai-metadata-box">
                        <div className="admin-field-label-row">
                          <div>
                            <label>سوالات متداول (FAQ)</label>
                            <small>برای نمایش در انتهای مقاله و ساخت FAQ Schema استفاده می‌شود.</small>
                          </div>
                          <button
                            type="button"
                            className="admin-text-action"
                            onClick={() => setArticleFormData((current) => ({
                              ...current,
                              faq: [...(current.faq || []), { question: '', answer: '' }],
                            }))}
                          >
                            <FiPlus /> افزودن سوال
                          </button>
                        </div>
                        <div className="admin-ai-faq-list">
                          {(articleFormData.faq || []).map((item, index) => (
                            <div className="admin-ai-faq-item" key={`article-faq-${index}`}>
                              <div className="admin-ai-translatable-control">
                                <label htmlFor={`article-faq-question-${index}`}>
                                  سوال
                                  <AdminFieldTranslation
                                    entityType="article"
                                    entityId={editingArticle?.id || editingArticle?._id}
                                    fieldPath={`faq.${index}.question`}
                                    fieldLabel={`سوال FAQ شماره ${index + 1}`}
                                    sourceValue={item.question || ''}
                                  />
                                </label>
                                <input
                                  id={`article-faq-question-${index}`}
                                  type="text"
                                  value={item.question || ''}
                                  onChange={(event) => setArticleFormData((current) => ({
                                    ...current,
                                    faq: current.faq.map((faqItem, faqIndex) => faqIndex === index ? { ...faqItem, question: event.target.value } : faqItem),
                                  }))}
                                  placeholder="سوال"
                                />
                              </div>
                              <div className="admin-ai-translatable-control">
                                <label htmlFor={`article-faq-answer-${index}`}>
                                  پاسخ
                                  <AdminFieldTranslation
                                    entityType="article"
                                    entityId={editingArticle?.id || editingArticle?._id}
                                    fieldPath={`faq.${index}.answer`}
                                    fieldLabel={`پاسخ FAQ شماره ${index + 1}`}
                                    sourceValue={item.answer || ''}
                                    multiline
                                  />
                                </label>
                                <textarea
                                  id={`article-faq-answer-${index}`}
                                  rows="3"
                                  value={item.answer || ''}
                                  onChange={(event) => setArticleFormData((current) => ({
                                    ...current,
                                    faq: current.faq.map((faqItem, faqIndex) => faqIndex === index ? { ...faqItem, answer: event.target.value } : faqItem),
                                  }))}
                                  placeholder="پاسخ"
                                />
                              </div>
                              <button
                                type="button"
                                className="admin-ai-remove"
                                onClick={() => setArticleFormData((current) => ({ ...current, faq: current.faq.filter((_, faqIndex) => faqIndex !== index) }))}
                              >
                                <FiTrash2 /> حذف
                              </button>
                            </div>
                          ))}
                          {(articleFormData.faq || []).length === 0 && <div className="admin-ai-empty-meta">FAQ برای این مقاله ثبت نشده است.</div>}
                        </div>
                      </div>
                      </section>
                    )}
                    {articleEditorTab === 'seo' && (
                      <section
                        className="admin-editor-panel admin-form-grid"
                        role="tabpanel"
                        id="article-editor-seo-panel"
                        aria-labelledby="article-editor-seo-tab"
                      >
                      <AdminEditorPanelHeading
                        title="تنظیمات SEO و نمایش در جستجو"
                        description="عنوان و توضیح مستقل مقاله را برای موتورهای جستجو و هر زبان تنظیم کنید."
                      />
                      <div className="admin-form-section-title">
                        <span><FiSearch aria-hidden="true" /></span>
                        <div>
                          <h4>تنظیمات سئو</h4>
                          <p>عنوان و توضیح مستقل این مقاله را برای هر زبان تنظیم کنید.</p>
                        </div>
                      </div>
                      <div className="admin-field admin-field-third">
                        <label htmlFor="article-publication-status">وضعیت انتشار</label>
                        <select id="article-publication-status" name="publicationStatus" value={articleFormData.publicationStatus} onChange={handleArticleFormChange}>
                          <option value="draft">پیش‌نویس</option>
                          <option value="published">منتشر شده</option>
                        </select>
                        <small>Draft در API عمومی و صفحات سایت نمایش داده نمی‌شود.</small>
                      </div>
                      <div className="admin-field admin-field-two-third">
                        <label htmlFor="article-slug">
                          Slug
                          <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="slug" fieldLabel="Slug" sourceValue={articleFormData.slug} />
                        </label>
                        <input id="article-slug" type="text" name="slug" value={articleFormData.slug} onChange={handleArticleFormChange} className="admin-ltr-input" placeholder="react-19-latest-updates" />
                      </div>
                      <div className="admin-field admin-field-third">
                        <label htmlFor="article-primary-keyword">
                          کلمه کلیدی اصلی
                          <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="primaryKeyword" fieldLabel="کلمه کلیدی اصلی" sourceValue={articleFormData.primaryKeyword} />
                        </label>
                        <input id="article-primary-keyword" type="text" name="primaryKeyword" value={articleFormData.primaryKeyword} onChange={handleArticleFormChange} placeholder="کلمه کلیدی اصلی" />
                      </div>
                      <div className="admin-field admin-field-two-third">
                        <label htmlFor="article-secondary-keywords">
                          کلمات کلیدی فرعی
                          <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="secondaryKeywords" fieldLabel="کلمات کلیدی فرعی" sourceValue={articleFormData.secondaryKeywords} multiline />
                        </label>
                        <input
                          id="article-secondary-keywords"
                          type="text"
                          value={(articleFormData.secondaryKeywords || []).join('، ')}
                          onChange={(event) => setArticleFormData((current) => ({
                            ...current,
                            secondaryKeywords: event.target.value.split(/[,،]/).map((item) => item.trim()).filter(Boolean),
                          }))}
                          placeholder="React 19، Server Components، Vite"
                        />
                      </div>
                      <div className="admin-field admin-field-full">
                        <label htmlFor="article-seo-title">
                          عنوان SEO
                          <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="seoTitle" fieldLabel="عنوان SEO" sourceValue={articleFormData.seoTitle} />
                        </label>
                        <input id="article-seo-title" type="text" name="seoTitle" value={articleFormData.seoTitle} onChange={handleArticleFormChange} maxLength="120" placeholder="عنوان پیشنهادی برای نتیجه جستجو" />
                        <small>{articleFormData.seoTitle.length.toLocaleString('fa-IR')} کاراکتر؛ حدود ۵۰ تا ۶۰ کاراکتر مناسب است.</small>
                      </div>
                      <div className="admin-field admin-field-full">
                        <label htmlFor="article-seo-description">
                          توضیحات SEO
                          <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="seoDescription" fieldLabel="توضیحات SEO" sourceValue={articleFormData.seoDescription} multiline />
                        </label>
                        <textarea id="article-seo-description" name="seoDescription" value={articleFormData.seoDescription} onChange={handleArticleFormChange} maxLength="320" rows="4" placeholder="خلاصه‌ای دقیق از مقاله برای موتورهای جستجو" />
                        <small>{articleFormData.seoDescription.length.toLocaleString('fa-IR')} کاراکتر؛ حدود ۱۴۰ تا ۱۶۰ کاراکتر مناسب است.</small>
                      </div>

                      <div className="admin-field admin-field-full admin-ai-metadata-box">
                        <label>Schemaهای فعال</label>
                        <div className="admin-ai-schema-list">
                          {['Article', 'TechArticle', 'NewsArticle', 'FAQPage', 'BreadcrumbList'].map((schemaType) => (
                            <label key={schemaType}>
                              <input
                                type="checkbox"
                                checked={(articleFormData.schemaTypes || []).includes(schemaType)}
                                onChange={(event) => setArticleFormData((current) => ({
                                  ...current,
                                  schemaTypes: event.target.checked
                                    ? [...new Set([...(current.schemaTypes || []), schemaType])]
                                    : (current.schemaTypes || []).filter((item) => item !== schemaType),
                                }))}
                              />
                              <span>{schemaType}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="admin-field admin-field-full admin-ai-metadata-box">
                        <label>CTA پیشنهادی</label>
                        <div className="admin-ai-cta-grid">
                          <div className="admin-ai-translatable-control">
                            <label htmlFor="article-cta-title">
                              عنوان CTA
                              <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="cta.title" fieldLabel="عنوان CTA" sourceValue={articleFormData.cta?.title || ''} />
                            </label>
                            <input id="article-cta-title" type="text" value={articleFormData.cta?.title || ''} onChange={(event) => setArticleFormData((current) => ({ ...current, cta: { ...current.cta, title: event.target.value } }))} placeholder="عنوان CTA" />
                          </div>
                          <div className="admin-ai-translatable-control">
                            <label htmlFor="article-cta-label">
                              متن دکمه
                              <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="cta.label" fieldLabel="متن دکمه CTA" sourceValue={articleFormData.cta?.label || ''} />
                            </label>
                            <input id="article-cta-label" type="text" value={articleFormData.cta?.label || ''} onChange={(event) => setArticleFormData((current) => ({ ...current, cta: { ...current.cta, label: event.target.value } }))} placeholder="متن دکمه" />
                          </div>
                          <div className="admin-ai-translatable-control">
                            <label htmlFor="article-cta-url">
                              آدرس CTA
                              <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="cta.url" fieldLabel="آدرس CTA" sourceValue={articleFormData.cta?.url || ''} />
                            </label>
                            <input id="article-cta-url" className="admin-ltr-input" type="text" value={articleFormData.cta?.url || ''} onChange={(event) => setArticleFormData((current) => ({ ...current, cta: { ...current.cta, url: event.target.value } }))} placeholder="/fa/contact" />
                          </div>
                          <div className="admin-ai-translatable-control admin-ai-cta-text-control">
                            <label htmlFor="article-cta-text">
                              متن CTA
                              <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="cta.text" fieldLabel="متن CTA" sourceValue={articleFormData.cta?.text || ''} multiline />
                            </label>
                            <textarea id="article-cta-text" rows="3" value={articleFormData.cta?.text || ''} onChange={(event) => setArticleFormData((current) => ({ ...current, cta: { ...current.cta, text: event.target.value } }))} placeholder="متن CTA" />
                          </div>
                        </div>
                      </div>

                      <div className="admin-field admin-field-full admin-ai-metadata-box">
                        <label>اتصال مقاله‌ها توسط Agent</label>
                        <div className="admin-ai-link-summary">
                          <strong>{(articleFormData.internalLinks || []).length.toLocaleString('fa-IR')} لینک داخلی</strong>
                          <strong>{(articleFormData.relatedArticles || []).length.toLocaleString('fa-IR')} مقاله مرتبط</strong>
                        </div>
                        {(articleFormData.internalLinks || []).map((link, index) => {
                          const linkedArticle = articles.find((article) => String(article.id || article._id) === String(link.article?._id || link.article?.id || link.article));
                          return (
                            <div className="admin-ai-linked-item" key={`internal-link-${index}`}>
                              <div><strong>{link.anchorText || linkedArticle?.title || 'لینک داخلی'}</strong><small>{linkedArticle?.title || link.reason || 'مقاله مرتبط در آرشیو'}</small></div>
                              <button type="button" onClick={() => setArticleFormData((current) => ({ ...current, internalLinks: current.internalLinks.filter((_, itemIndex) => itemIndex !== index) }))}><FiX /></button>
                            </div>
                          );
                        })}
                        {(articleFormData.relatedArticles || []).map((articleId, index) => {
                          const related = articles.find((article) => String(article.id || article._id) === String(articleId?._id || articleId?.id || articleId));
                          return (
                            <div className="admin-ai-linked-item" key={`related-link-${index}`}>
                              <div><strong>{related?.title || 'مقاله مرتبط'}</strong><small>پیشنهاد Related Articles</small></div>
                              <button type="button" onClick={() => setArticleFormData((current) => ({ ...current, relatedArticles: current.relatedArticles.filter((_, itemIndex) => itemIndex !== index) }))}><FiX /></button>
                            </div>
                          );
                        })}
                      </div>
                      </section>
                    )}
                    {articleEditorTab === 'media' && (
                      <section
                        className="admin-editor-panel admin-form-grid"
                        role="tabpanel"
                        id="article-editor-media-panel"
                        aria-labelledby="article-editor-media-tab"
                      >
                      <AdminEditorPanelHeading
                        title="تصویر شاخص مقاله"
                        description="تصویر افقی کارت و سربرگ مقاله را آپلود یا از طریق آدرس مستقیم انتخاب کنید."
                      />
                      <div className="admin-field admin-field-full">
                        <div className="admin-field-label-row admin-product-images-heading">
                          <div>
                            <label>تصویر شاخص مقاله</label>
                            <small>تصویر افقی با نسبت پیشنهادی ۱۶:۹ برای کارت و سربرگ مقاله انتخاب کنید.</small>
                          </div>
                          <span className="admin-image-count-badge">تصویر افقی</span>
                        </div>
                        <section className="admin-product-image-group admin-article-image-group">
                          <div className="admin-product-image-editor">
                            <div className="admin-upload-column">
                              <input
                                id="article-featured-image-upload"
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                                className="admin-upload-input"
                                onChange={handleArticleFeaturedImageUpload}
                                disabled={articleImageUploading}
                              />
                              <label htmlFor="article-featured-image-upload" className={`admin-upload-zone ${articleImageUploading ? 'uploading' : ''}`}>
                                <FiUploadCloud aria-hidden="true" />
                                <strong>{articleImageUploading ? 'در حال آپلود تصویر...' : 'آپلود از کامپیوتر'}</strong>
                                <span>JPG، PNG، WEBP، GIF یا AVIF تا ۵ مگابایت</span>
                              </label>
                              {articleImageError && <p className="admin-upload-error">{articleImageError}</p>}
                            </div>
                            <div className="admin-image-url-column">
                              <label htmlFor="article-image-url">یا آدرس مستقیم تصویر</label>
                              <input
                                id="article-image-url"
                                type="url"
                                name="imageUrl"
                                value={getDirectMediaUrlInputValue(articleFormData.imageUrl)}
                                onChange={handleArticleFormChange}
                                className="admin-ltr-input"
                                placeholder={articleFormData.imageUrl.startsWith('/')
                                  ? 'برای جایگزینی تصویر، یک آدرس کامل وارد کنید'
                                  : 'https://example.com/article.webp'}
                              />
                            </div>
                          </div>
                          {articleFormData.imageUrl ? (
                            <div className="admin-product-image-preview is-banner">
                              <img src={resolveMediaUrl(articleFormData.imageUrl)} alt="پیش‌نمایش تصویر شاخص مقاله" />
                              <div>
                                <strong>پیش‌نمایش تصویر شاخص</strong>
                                <span>{articleFormData.imageUrl.startsWith('/uploads/') ? 'آپلود شده از کامپیوتر' : 'بارگذاری شده از نشانی اینترنتی'}</span>
                              </div>
                              <button type="button" onClick={() => setArticleFormData((current) => ({ ...current, imageUrl: '' }))}>
                                <FiTrash2 aria-hidden="true" /> حذف تصویر
                              </button>
                            </div>
                          ) : (
                            <div className="admin-product-image-empty">هنوز تصویر شاخصی برای این مقاله انتخاب نشده است.</div>
                          )}
                        </section>
                      </div>
                      <div className="admin-field admin-field-full">
                        <label htmlFor="article-image-alt">
                          Alt تصویر شاخص
                          <AdminFieldTranslation entityType="article" entityId={editingArticle?.id || editingArticle?._id} fieldPath="imageAlt" fieldLabel="Alt تصویر شاخص" sourceValue={articleFormData.imageAlt} />
                        </label>
                        <input id="article-image-alt" type="text" name="imageAlt" value={articleFormData.imageAlt} onChange={handleArticleFormChange} placeholder="توضیح دقیق و قابل فهم تصویر برای SEO و دسترس‌پذیری" />
                      </div>
                      </section>
                    )}
                    <div className="admin-form-actions article-editor-actions">
                      {editingArticle ? (
                        <AdminArticleTranslationPublish
                          articleId={editingArticle.id || editingArticle._id}
                          articlePublicationStatus={editingArticle.publicationStatus}
                        />
                      ) : null}
                      <div className="article-editor-form-buttons">
                        <button type="submit" className="admin-primary-action" disabled={articleImageUploading || articleCategoryBusy}>
                          <FiCheck aria-hidden="true" />
                          {editingArticle ? 'ذخیره تغییرات مقاله' : 'ایجاد مقاله جدید'}
                        </button>
                        <button type="button" className="admin-secondary-action" onClick={closeArticleForm}>
                          <FiArrowRight aria-hidden="true" />
                          بازگشت بدون ذخیره
                        </button>
                      </div>
                    </div>
                  </form>
                </AdminFormModal>
              )}

              <div className="search-section">
                <FiSearch aria-hidden="true" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="جستجو بر اساس عنوان، خلاصه یا دسته‌بندی..."
                  value={articleSearchTerm}
                  onChange={(e) => setArticleSearchTerm(e.target.value)}
                />
              </div>

              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>تصویر</th>
                      <th>عنوان</th>
                      <th>دسته‌بندی</th>
                      <th>تعامل</th>
                      <th>خلاصه</th>
                      <th>تاریخ</th>
                      <th>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredArticles.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                          {articleSearchTerm ? 'نتیجه‌ای یافت نشد' : 'هیچ مقاله‌ای ثبت نشده است'}
                        </td>
                      </tr>
                    ) : (
                      filteredArticles.map((article) => (
                        <tr key={article.id || article._id}>
                          <td>
                            <div className="admin-article-thumbnail">
                              {article.imageUrl ? (
                                <img src={resolveMediaUrl(article.imageUrl)} alt={article.imageAlt || article.title} />
                              ) : (
                                <FiImage aria-hidden="true" />
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="admin-article-title-cell">
                              <strong>{article.title}</strong>
                              {article.publicationStatus === 'draft' && <span className="admin-article-status is-draft">پیش‌نویس</span>}
                              {article.generatedByAi && <span className="admin-article-status is-ai"><FiCpu /> AI</span>}
                            </div>
                          </td>
                          <td>
                            <span className="article-category-badge">{article.category}</span>
                          </td>
                          <td>
                            <div className="admin-article-metrics">
                              <span title="تعداد بازدید"><FiEye aria-hidden="true" /> {Number(article.views || 0).toLocaleString('fa-IR')}</span>
                              <span title="تعداد لایک"><FiHeart aria-hidden="true" /> {Number(article.likesCount || 0).toLocaleString('fa-IR')}</span>
                            </div>
                          </td>
                          <td><p className="admin-article-excerpt">{article.excerpt}</p></td>
                          <td>{article.date || (article.createdAt ? new Date(article.createdAt).toLocaleDateString('fa-IR') : '—')}</td>
                          <td>
                            <div className="action-buttons">
                              <button type="button" className="edit-btn" onClick={() => handleEditArticle(article)}><FiEdit2 /> ویرایش</button>
                              <button type="button" className="delete-btn" onClick={() => handleDeleteArticle(article.id || article._id)}><FiTrash2 /> حذف</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div key="orders" className="admin-content">
            <div className="section-panel">
              <div className="panel-header-section">
                <div className="panel-heading">
                  <span className="panel-heading-icon"><FiShoppingBag /></span>
                  <div>
                    <h2>فهرست سفارشات</h2>
                    <p>{orders.length.toLocaleString('fa-IR')} سفارش برای پیگیری در سیستم وجود دارد.</p>
                  </div>
                </div>
                <button type="button" className="add-btn" onClick={handleAddOrder}><FiPlus /> افزودن سفارش جدید</button>
              </div>

              {showOrderForm && (
                <AdminFormModal
                  title={editingOrder ? 'ویرایش سفارش' : 'افزودن سفارش جدید'}
                  eyebrow="مدیریت سفارش‌ها"
                  description="سفارش محصول دانلودی، وضعیت پرداخت و دسترسی دانلود را مدیریت کنید."
                  icon={<FiShoppingBag />}
                  onClose={closeOrderForm}
                >
                  <form onSubmit={handleSubmitOrder} className="admin-entity-form">
                    <div className="admin-form-note">
                      <FiShoppingBag aria-hidden="true" />
                      <div>
                        <strong>ثبت دستی سفارش</strong>
                        <span>تنها کاربران و محصولاتی که قبلاً ثبت شده‌اند در فهرست‌های زیر نمایش داده می‌شوند.</span>
                      </div>
                    </div>
                    <div className="admin-form-grid">
                      <div className="admin-field admin-field-half">
                        <label htmlFor="order-customer">مشتری <span>*</span></label>
                        <select
                          id="order-customer"
                          name="customerId"
                          value={orderFormData.customerId}
                          onChange={(e) => setOrderFormData({ ...orderFormData, customerId: e.target.value })}
                          disabled={Boolean(editingOrder && Number(editingOrder.paidAmount) > 0)}
                          required
                          autoFocus
                        >
                          <option value="">انتخاب مشتری</option>
                          {users.map((u) => (
                            <option key={u.id || u._id} value={u.id || u._id}>{u.name} ({u.email})</option>
                          ))}
                        </select>
                        <small>حساب کاربری مرتبط با سفارش را انتخاب کنید.</small>
                      </div>
                      <div className="admin-field admin-field-half">
                        <label htmlFor="order-product">محصول <span>*</span></label>
                        <select
                          id="order-product"
                          name="productId"
                          value={orderFormData.productId}
                          onChange={(e) => setOrderFormData({ ...orderFormData, productId: e.target.value })}
                          disabled={Boolean(editingOrder && Number(editingOrder.paidAmount) > 0)}
                          required
                        >
                          <option value="">انتخاب محصول</option>
                          {products.map((p) => (
                            <option key={p.id || p._id} value={p.id || p._id}>{p.name}</option>
                          ))}
                        </select>
                        <small>محصولی که مشتری سفارش داده است را انتخاب کنید.</small>
                      </div>
                      <div className="admin-field admin-field-half">
                        <label htmlFor="order-amount">مبلغ کل سفارش <span>*</span></label>
                        <div className="admin-input-with-suffix">
                          <input
                            id="order-amount"
                            type="text"
                            inputMode="numeric"
                            name="amount"
                            value={orderFormData.amount}
                            onChange={handleOrderFormChange}
                            disabled={Boolean(editingOrder && Number(editingOrder.paidAmount) > 0)}
                            required
                            placeholder="مثلاً 1,500,000"
                          />
                          <span>تومان</span>
                        </div>
                        <small>پس از ثبت نخستین پرداخت، مبلغ سفارش برای حفظ سوابق مالی قفل می‌شود.</small>
                      </div>
                      <div className="admin-field admin-field-half">
                        <label htmlFor="order-status">وضعیت سفارش <span>*</span></label>
                        <select
                          id="order-status"
                          name="status"
                          value={orderFormData.status}
                          onChange={handleOrderFormChange}
                          required
                        >
                          {(editingOrder
                            ? PRODUCT_ORDER_STATUSES
                            : ['در انتظار پرداخت', 'لغو شده']
                          ).map((status) => (
                            <option value={status} key={status}>{status}</option>
                          ))}
                        </select>
                        <small>«آماده دانلود» فقط بعد از تسویه کامل فعال می‌شود.</small>
                      </div>
                      {editingOrder && (
                        <div className="admin-field admin-field-full admin-order-payment-manager">
                          <div className="admin-order-payment-heading">
                            <span><FiCreditCard /></span>
                            <div>
                              <strong>ثبت و تأیید پرداخت دستی</strong>
                              <small>مبالغ کارت‌به‌کارت یا واریز بانکی را اینجا ثبت کنید.</small>
                            </div>
                          </div>
                          <div className="admin-order-payment-summary">
                            <div><small>مبلغ سفارش</small><strong>{Number(editingOrder.amount || 0).toLocaleString('fa-IR')} {editingOrder.currencyLabel || 'تومان'}</strong></div>
                            <div><small>پرداخت‌شده</small><strong>{Number(editingOrder.paidAmount || 0).toLocaleString('fa-IR')} {editingOrder.currencyLabel || 'تومان'}</strong></div>
                            <div><small>مانده</small><strong>{Number(editingOrder.remainingAmount || 0).toLocaleString('fa-IR')} {editingOrder.currencyLabel || 'تومان'}</strong></div>
                            <div><small>وضعیت پرداخت</small><strong>{editingOrder.paymentStatus || 'پرداخت نشده'}</strong></div>
                          </div>
                          {editingOrder.paymentStatus !== 'پرداخت شده' && editingOrder.paymentStatus !== 'بازگشت وجه' && (
                            <div className="admin-order-manual-payment-grid">
                              <label>
                                مبلغ پرداخت‌شده
                                <div className="admin-input-with-suffix">
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    value={manualOrderPayment.amount}
                                    onChange={(event) => setManualOrderPayment({ ...manualOrderPayment, amount: event.target.value })}
                                    placeholder="مبلغ تأییدشده"
                                  />
                                  <span>{editingOrder.currencyLabel || 'تومان'}</span>
                                </div>
                              </label>
                              <label>
                                تاریخ پرداخت
                                <input
                                  type="date"
                                  value={manualOrderPayment.paidAt}
                                  onChange={(event) => setManualOrderPayment({ ...manualOrderPayment, paidAt: event.target.value })}
                                />
                              </label>
                              <label>
                                روش پرداخت
                                <select
                                  value={manualOrderPayment.paymentMethod}
                                  onChange={(event) => setManualOrderPayment({ ...manualOrderPayment, paymentMethod: event.target.value })}
                                >
                                  <option value="card_to_card">کارت‌به‌کارت</option>
                                  <option value="bank_transfer">واریز بانکی</option>
                                  <option value="cash">نقدی</option>
                                  <option value="other">سایر</option>
                                </select>
                              </label>
                              <label>
                                شماره پیگیری
                                <input
                                  value={manualOrderPayment.reference}
                                  onChange={(event) => setManualOrderPayment({ ...manualOrderPayment, reference: event.target.value })}
                                  placeholder="اختیاری"
                                />
                              </label>
                              <label className="is-wide">
                                توضیح برای کاربر
                                <textarea
                                  rows="2"
                                  value={manualOrderPayment.note}
                                  onChange={(event) => setManualOrderPayment({ ...manualOrderPayment, note: event.target.value })}
                                  placeholder="اختیاری"
                                />
                              </label>
                              <button
                                type="button"
                                className="admin-order-payment-submit"
                                onClick={handleManualOrderPayment}
                                disabled={orderPaymentBusy}
                              >
                                <FiCheck />
                                {orderPaymentBusy ? 'در حال ثبت...' : 'تأیید و ثبت پرداخت'}
                              </button>
                            </div>
                          )}
                          {orderPaymentError && <p className="admin-order-payment-error">{orderPaymentError}</p>}
                          {editingOrder.paymentStatus === 'پرداخت شده' && (
                            <p className="admin-order-download-ready"><FiCheckCircle /> پرداخت کامل است و دانلود محصول برای کاربر فعال شده است.</p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="admin-form-actions">
                        <button type="submit" className="admin-primary-action">
                          <FiCheck aria-hidden="true" />
                          {editingOrder ? 'ذخیره تغییرات سفارش' : 'ثبت سفارش جدید'}
                        </button>
                        <button type="button" className="admin-secondary-action" onClick={closeOrderForm}>
                          <FiArrowRight aria-hidden="true" />
                          بازگشت بدون ذخیره
                        </button>
                    </div>
                  </form>
                </AdminFormModal>
              )}

              <div className="search-section">
                <FiSearch aria-hidden="true" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="جستجو بر اساس مشتری، محصول یا وضعیت..."
                  value={orderSearchTerm}
                  onChange={(e) => setOrderSearchTerm(e.target.value)}
                />
              </div>

              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>شناسه</th>
                      <th>مشتری</th>
                      <th>محصول</th>
                      <th>مبلغ</th>
                      <th>وضعیت سفارش</th>
                      <th>وضعیت پرداخت</th>
                      <th>تاریخ ثبت</th>
                      <th>عملیات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                          {orderSearchTerm ? 'نتیجه‌ای یافت نشد' : 'هیچ سفارشی ثبت نشده است'}
                        </td>
                      </tr>
                    ) : (
                      filteredOrders.map((order) => (
                        <tr key={order.id || order._id}>
                          <td>#{order.orderNumber || order.id || order._id}</td>
                          <td>{orderCustomerName(order)}</td>
                          <td>{orderProductName(order)}</td>
                          <td>{(typeof order.amount === 'number' ? order.amount.toLocaleString('fa-IR') : order.amount) || '—'} {order.currencyLabel || 'تومان'}</td>
                          <td>
                            <span className={`status-badge status-${(order.status || '').replace(/ /g, '-')}`}>
                              {order.status || '—'}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge status-${(order.paymentStatus || '').replace(/ /g, '-')}`}>
                              {order.paymentStatus || 'پرداخت نشده'}
                            </span>
                            <small className="admin-order-payment-cell">
                              {Number(order.paidAmount || 0).toLocaleString('fa-IR')} از {Number(order.amount || 0).toLocaleString('fa-IR')} {order.currencyLabel || 'تومان'}
                            </small>
                          </td>
                          <td>{order.createdAt ? new Date(order.createdAt).toLocaleDateString('fa-IR') : '—'}</td>
                          <td>
                            <div className="action-buttons">
                              <button type="button" className="edit-btn" onClick={() => handleEditOrder(order)}><FiEdit2 /> ویرایش</button>
                              <button type="button" className="delete-btn" onClick={() => handleDeleteOrder(order.id || order._id)}><FiTrash2 /> حذف</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'service-orders' && (
          <div key="service-orders" className="admin-content">
            <div className="section-panel">
              <AdminServiceOrders
                onCountChange={setServiceOrderCount}
                initialOrderId={serviceOrderToOpen}
                onInitialOrderHandled={clearServiceOrderToOpen}
              />
            </div>
          </div>
        )}

        {activeTab === 'finance' && (
          <div key="finance" className="admin-content">
            <div className="section-panel">
              <AdminTransactions
                onCountChange={setTransactionCount}
                onOpenOrder={handleOpenFinancialOrder}
              />
            </div>
          </div>
        )}

        {activeTab === 'contact-messages' && (
          <div key="contact-messages" className="admin-content">
            <div className="section-panel"><AdminContactMessages onCountChange={setContactMessageCount} /></div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div key="reviews" className="admin-content">
            <div className="section-panel">
              <AdminProductReviews products={products} onCountChange={setProductReviewCount} />
            </div>
          </div>
        )}

        {activeTab === 'backups' && (
          <div key="backups" className="admin-content">
            <AdminBackups />
          </div>
        )}

        {activeTab === 'locales' && (
          <div key="locales" className="admin-content">
            <AdminLocales />
          </div>
        )}

        {activeTab === 'settings' && (
          <div key="settings" className="admin-content">
            <AdminSettingsPanel />
          </div>
        )}

        {/* Settings Modal */}
        {false && showSettings && (
          <div className="user-form-modal">
            <div className="user-form-container" style={{ maxWidth: '600px' }}>
              <div className="form-header">
                <h3>تنظیمات وب سایت</h3>
                <button type="button" className="close-btn ui-close-button" onClick={() => setShowSettings(false)} aria-label="بستن تنظیمات">✕</button>
              </div>
              <form onSubmit={handleSaveSettings} className="user-form">
                <div className="form-group">
                  <label>عنوان وب سایت</label>
                  <input
                    type="text"
                    name="siteTitle"
                    value={settings.siteTitle}
                    onChange={handleSettingsChange}
                    required
                    placeholder="عنوان وب سایت"
                  />
                </div>
                <div className="form-group">
                  <label>لوگو</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                        آپلود از کامپیوتر:
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '5px',
                          fontSize: '0.9rem'
                        }}
                      />
                      <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                        حداکثر حجم: 2 مگابایت (فرمت‌های مجاز: JPG, PNG, GIF, SVG)
                      </p>
                    </div>
                    <div style={{ textAlign: 'center', color: '#666', fontSize: '0.9rem' }}>یا</div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                        آدرس لوگو (URL):
                      </label>
                      <input
                        type="url"
                        name="logoUrl"
                        value={settings.logoUrl && !settings.logoUrl.startsWith('data:') ? settings.logoUrl : ''}
                        onChange={handleSettingsChange}
                        placeholder="https://example.com/logo.png"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '5px'
                        }}
                      />
                    </div>
                  </div>
                  {settings.logoUrl && (
                    <div className="image-preview" style={{ marginTop: '1rem' }}>
                      <img 
                        src={settings.logoUrl} 
                        alt="Logo Preview" 
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'block';
                          }
                        }}
                        style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                      />
                      <p style={{ display: 'none', color: '#e74c3c', fontSize: '0.85rem' }}>
                        خطا در نمایش تصویر
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setSettings({
                            ...settings,
                            logoUrl: ''
                          });
                        }}
                        style={{
                          marginTop: '0.5rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        حذف لوگو
                      </button>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>رنگ اصلی تیره (Primary Dark)</label>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <input
                      type="color"
                      name="primaryDark"
                      value={settings.primaryDark}
                      onChange={handleSettingsChange}
                      style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      name="primaryDark"
                      value={settings.primaryDark}
                      onChange={handleSettingsChange}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>رنگ اصلی (Primary)</label>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <input
                      type="color"
                      name="primary"
                      value={settings.primary}
                      onChange={handleSettingsChange}
                      style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      name="primary"
                      value={settings.primary}
                      onChange={handleSettingsChange}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>رنگ اصلی روشن (Primary Light)</label>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <input
                      type="color"
                      name="primaryLight"
                      value={settings.primaryLight}
                      onChange={handleSettingsChange}
                      style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      name="primaryLight"
                      value={settings.primaryLight}
                      onChange={handleSettingsChange}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>رنگ ثانویه (Secondary)</label>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <input
                      type="color"
                      name="secondary"
                      value={settings.secondary}
                      onChange={handleSettingsChange}
                      style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      name="secondary"
                      value={settings.secondary}
                      onChange={handleSettingsChange}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>رنگ ثانویه روشن (Secondary Light)</label>
                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <input
                      type="color"
                      name="secondaryLight"
                      value={settings.secondaryLight}
                      onChange={handleSettingsChange}
                      style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      name="secondaryLight"
                      value={settings.secondaryLight}
                      onChange={handleSettingsChange}
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="submit" className="submit-user-btn">
                    ذخیره تنظیمات
                  </button>
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={() => setShowSettings(false)}
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
